import { create } from "zustand";
import {
  createChatConversation,
  createChatMessage,
  updateChatConversation,
  updateChatConversationState,
  updateChatMessage,
  deleteChatMessage,
  deleteChatConversation,
  listChatConversations,
  listChatMessages,
  useUserStore,
} from "@slumbr/core";
import type {
  Conversation,
  Message,
  MessageContent,
  ChatParticipant,
  ConversationSortField,
  SortDirection,
  TypingIndicator,
  SystemContent,
  ChatTheme,
  ConversationEncryptionState,
  EncryptionSettings,
  PinLockConfig,
  EncryptionDisplayInfo,
  CallType,
  CallSession,
  CallParticipant,
  LocalCallState,
} from "../types";
import type { EmojiSet, CustomEmojiPack } from "../components/emoji-data";
import { generateUUID } from "@/utils/uuid";
import { toUnixMs as msTime } from "../utils/timestamps";
import {
  generateIdentityKeyPair,
  generateSignedPreKey,
  generateOneTimePreKeys,
  buildPreKeyBundle,
  createDevSession,
  encryptMessage,
  decryptMessage,
  generateSafetyNumber,
  computeFingerprint,
  type EncryptionSession,
} from "../crypto/signal-protocol";
import * as keyStore from "../crypto/key-store";
import {
  getCachedConversations,
  setCachedConversations,
  getCachedRecentMessages,
  putCachedMessages,
  deleteCachedMessage,
  deleteCachedConversation,
  getConversationMeta,
  setConversationMeta,
  clearMessengerCache,
  setActiveProfile,
} from "../storage/idb-cache";
import { useProfileSwitcherStore } from "@/stores/profile-switcher-store";
import { isMessageVisible, mergeMessages } from "./message-sync";

// ─── Dev Auth Bypass ─────────────────────────────────────────────────────────

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

// ─── localStorage persistence ────────────────────────────────────────────────

const STORAGE_KEYS = {
  CONVERSATIONS: "slumbr:messenger:conversations",
  MESSAGES: "slumbr:messenger:messages",
  ENCRYPTION_STATES: "slumbr:messenger:encryptionStates",
  ENCRYPTION_SETTINGS: "slumbr:messenger:encryptionSettings",
} as const;

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const stored = localStorage.getItem(key);
    if (!stored) return fallback;
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

function saveToStorage(key: string, data: unknown): void {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(key, JSON.stringify(data));
  } catch (err) {
    console.error(`Failed to save ${key} to localStorage:`, err);
  }
}

// ── Server clock offset ───────────────────────────────────────────────────
// The server is the sole source of truth for message `createdAt` /
// `updatedAt` — `globalMessageKey` derives the DDB sort key from
// createdAt, so a client whose local clock is off by even a minute (DST
// drift, manual clock change, VM clock skew, no NTP) would otherwise
// place its messages out of order in every reader's chat. We track an
// offset between the local clock and the most recent server timestamp
// we've observed so that optimistic-render `createdAt` values stay
// aligned with what the server is about to assign. Any timestamp we
// stamp into outgoing messages, and any timestamp we render on the
// sender's screen before the server response arrives, uses
// `getApproxServerNowMs()` rather than `Date.now()`.
let serverTimeOffsetMs = 0;

export function recordServerTimestamp(timestamp: unknown) {
  const t =
    typeof timestamp === "number"
      ? timestamp
      : typeof timestamp === "string"
        ? Date.parse(timestamp)
        : Number.NaN;
  if (!Number.isFinite(t)) return;
  serverTimeOffsetMs = t - Date.now();
}

function getApproxServerNowMs(): number {
  return Date.now() + serverTimeOffsetMs;
}

/** Replace the optimistic timestamps on a locally-inserted message with
 *  the authoritative values returned by the server. Re-sorts the
 *  message list so an hour-skewed clock can't leave the new message
 *  parked in the wrong slot. */
function reconcileMessageTimestamps(
  conversationId: string,
  messageId: string,
  serverCreatedAt: unknown,
  serverUpdatedAt: unknown
) {
  const createdAt =
    typeof serverCreatedAt === "number"
      ? serverCreatedAt
      : typeof serverCreatedAt === "string"
        ? Date.parse(serverCreatedAt)
        : Number.NaN;
  const updatedAt =
    typeof serverUpdatedAt === "number"
      ? serverUpdatedAt
      : typeof serverUpdatedAt === "string"
        ? Date.parse(serverUpdatedAt)
        : createdAt;
  if (!Number.isFinite(createdAt)) return;

  useMessengerStore.setState((s) => {
    const list = s.messages[conversationId];
    if (!list) return s;
    const next = list
      .map((m) =>
        m.id === messageId
          ? {
              ...m,
              createdAt,
              updatedAt: Number.isFinite(updatedAt) ? updatedAt : createdAt,
            }
          : m
      )
      .sort((a, b) => msTime(a.createdAt) - msTime(b.createdAt));
    const messages = { ...s.messages, [conversationId]: next };
    const updated = next.find((m) => m.id === messageId);
    const conversations = updated
      ? s.conversations.map((c) =>
          c.id === conversationId && c.lastMessage?.id === messageId
            ? { ...c, lastMessage: updated, updatedAt: createdAt }
            : c
        )
      : s.conversations;
    return { conversations, messages };
  });
  const fresh = useMessengerStore.getState().messages[conversationId];
  if (fresh) void putCachedMessages(conversationId, fresh);
}

// ── IDB cache persistence ─────────────────────────────────────────────────
// Mirror the store's messages/conversations into IndexedDB so subsequent
// app loads and conversation switches can paint from cache instantly while
// the server fetch runs in the background.
//
// We track the last-persisted reference for each slice and only re-write
// the parts that actually changed. Zustand updates are immutable, so a
// reference equality check is enough: a new array means a real mutation,
// the same reference means nothing relevant happened for that slice.
let idbPersistTimer: ReturnType<typeof setTimeout> | null = null;
let prevPersistedConversations: Conversation[] | null = null;
const prevPersistedMessagesByConv: Record<string, Message[]> = {};

// Conversation-list fetch coordination: dedupe overlapping callers and
// remember when the list last synced so `resync` can debounce itself.
// The epoch increments on profile switch / logout so a fetch that was in
// flight for the OLD profile can never write its result into the NEW
// profile's store.
let inFlightConversationsFetch: Promise<void> | null = null;
let lastConversationsSyncAt = 0;
let messengerResetEpoch = 0;
const historyFetches = new Map<string, Promise<void>>();
const pendingConversationCreates = new Map<string, Promise<unknown>>();
const pendingMessagePublishes = new Map<string, Promise<void>>();
const readTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Per-conversation debounce for the server-side read marker.
const lastMarkReadSentAt = new Map<string, number>();
function scheduleIdbPersist(state: {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
}) {
  if (typeof window === "undefined") return;
  if (idbPersistTimer) clearTimeout(idbPersistTimer);
  const convsRef = state.conversations;
  const messagesRef = state.messages;
  idbPersistTimer = setTimeout(() => {
    if (convsRef !== prevPersistedConversations) {
      void setCachedConversations(convsRef);
      prevPersistedConversations = convsRef;
    }
    for (const convId in messagesRef) {
      const list = messagesRef[convId];
      if (list === prevPersistedMessagesByConv[convId]) continue;
      // Strip in-flight AND failed uploads — client-only state that
      // shouldn't survive a reload (a failed upload's blob: URL is dead
      // after reload, so persisting it re-showed a permanently broken
      // bubble on every boot).
      const clean = list.filter(
        (m) => !m.uploadState
      );
      void putCachedMessages(convId, clean);
      prevPersistedMessagesByConv[convId] = list;
    }
  }, 400);
}

// ─── Current user (dev mode) ─────────────────────────────────────────────────

const DEV_USER_ID = "dev-user";
const DEV_USER_NAME = "You";

export function getCurrentUserId(): string {
  if (DEV_AUTH_BYPASS) return DEV_USER_ID;
  return useUserStore.getState().user?.id ?? DEV_USER_ID;
}

export function getCurrentUserName(): string {
  if (DEV_AUTH_BYPASS) return DEV_USER_NAME;
  return useUserStore.getState().user?.displayName ?? DEV_USER_NAME;
}

export function getCurrentUserAvatar(): string | undefined {
  if (DEV_AUTH_BYPASS) return undefined;
  const avatar = useUserStore.getState().user?.avatar;
  return avatar && avatar.length > 0 ? avatar : undefined;
}

// ─── Encryption defaults ─────────────────────────────────────────────────────

const DEFAULT_PIN_LOCK_CONFIG: PinLockConfig = {
  isEnabled: false,
  pinHash: null,
  autoLockTimeout: 5 * 60 * 1000, // 5 minutes
  lockOnTabHidden: true,
  maxAttempts: 5,
  failedAttempts: 0,
  lockoutUntil: null,
};

const DEFAULT_ENCRYPTION_SETTINGS: EncryptionSettings = {
  identityFingerprint: null,
  hasIdentityKeys: false,
  lastKeyRotation: null,
  pinLock: DEFAULT_PIN_LOCK_CONFIG,
};

/** Hash a PIN string using SHA-256 */
async function hashPin(pin: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + "slumbr-pin-salt");
  const hash = await crypto.subtle.digest("SHA-256", data);
  const bytes = new Uint8Array(hash);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ─── Mock seed data ──────────────────────────────────────────────────────────

function createMockParticipant(
  userId: string,
  displayName: string,
  role: ChatParticipant["role"] = "member",
  avatarUrl?: string
): ChatParticipant {
  return {
    userId,
    displayName,
    avatarUrl,
    role,
    joinedAt: new Date(Date.now() - 86400000 * 30).toISOString(),
    lastReadAt: new Date().toISOString(),
    isMuted: false,
  };
}

/**
 * Strip empty-string avatarUrl before sending to the backend — the schema
 * enforces .url() validation so an empty string fails. We omit the field
 * entirely when it isn't a real URL.
 */
function serializeParticipant(p: ChatParticipant): Record<string, unknown> {
  const { avatarUrl, ...rest } = p;
  return avatarUrl && avatarUrl.length > 0 ? { ...rest, avatarUrl } : rest;
}

function stripClientMessageFields(message: Message): Message {
  const { uploadState, uploadProgress, uploadError, ...rest } = message;
  return rest.isEncrypted && rest.encryptedEnvelope ? { ...rest, content: { type: "encrypted" } } : rest;
}

/** Every send/retry waits for conversation creation and is fenced to its identity. */
function publishMessage(conversationId: string, message: Message): void {
  if (pendingMessagePublishes.has(message.id)) return;
  const epoch = messengerResetEpoch;
  const attempt = (async () => {
    try {
      await pendingConversationCreates.get(conversationId);
      if (epoch !== messengerResetEpoch) return;
      const outbound = stripClientMessageFields(message) as unknown as Record<string, unknown>;
      delete outbound.createdAt;
      delete outbound.updatedAt;
      const response = DEV_AUTH_BYPASS ? message : await createChatMessage(conversationId, outbound);
      if (epoch !== messengerResetEpoch) return;
      const ack = response as Partial<Message> | undefined;
      if (ack?.createdAt !== undefined) reconcileMessageTimestamps(conversationId, message.id, ack.createdAt, ack.updatedAt ?? ack.createdAt);
      const store = useMessengerStore.getState();
      const current = store.messages[conversationId]?.find((m) => m.id === message.id);
      if (!current) return;
      store.updateLocalMessage(conversationId, message.id, {
        status: current.status === "read" ? "read" : "delivered",
        uploadState: undefined,
        uploadError: undefined,
        ...(ack?.expiresAt !== undefined ? { expiresAt: ack.expiresAt } : {}),
      });
    } catch (err) {
      if (epoch !== messengerResetEpoch) return;
      useMessengerStore.getState().updateLocalMessage(conversationId, message.id, {
        status: "failed",
        // The attachment has already uploaded. Retrying publication is safe.
        uploadState: undefined,
        uploadError: err instanceof Error ? err.message : "Send failed",
      });
    }
  })();
  pendingMessagePublishes.set(message.id, attempt);
  void attempt.finally(() => {
    if (pendingMessagePublishes.get(message.id) === attempt) pendingMessagePublishes.delete(message.id);
  });
}

function syncMessageUpdate(conversationId: string, messageId: string, updates: Record<string, unknown>, previous?: Message): void {
  if (DEV_AUTH_BYPASS) return;
  const epoch = messengerResetEpoch;
  const optimistic = useMessengerStore.getState().messages[conversationId]?.find((message) => message.id === messageId);
  void updateChatMessage(conversationId, messageId, updates).then((response) => {
    if (epoch !== messengerResetEpoch) return;
    const store = useMessengerStore.getState();
    const current = store.messages[conversationId]?.find((message) => message.id === messageId);
    if (current !== optimistic || !response || typeof response !== "object" || !("id" in response)) return;
    store.updateLocalMessage(conversationId, messageId, response as Message);
  }).catch(() => {
    if (epoch !== messengerResetEpoch) return;
    const store = useMessengerStore.getState();
    if (previous && store.messages[conversationId]?.find((message) => message.id === messageId) === optimistic) {
      store.updateLocalMessage(conversationId, messageId, previous);
    }
    useMessengerStore.setState((state) => ({ messageErrors: { ...state.messageErrors, [conversationId]: "Your change wasn't saved. Please try again." } }));
  });
}

const MOCK_PARTICIPANTS = {
  self: createMockParticipant(DEV_USER_ID, DEV_USER_NAME, "owner"),
  alice: createMockParticipant("user-alice", "Alice Chen"),
  bob: createMockParticipant("user-bob", "Bob Martinez"),
  carol: createMockParticipant("user-carol", "Carol Kim"),
  dave: createMockParticipant("user-dave", "Dave Wilson"),
  eve: createMockParticipant("user-eve", "Eve Parker"),
};

function createMockMessage(
  id: string,
  conversationId: string,
  senderId: string,
  text: string,
  createdAt: Date
): Message {
  return {
    id,
    conversationId,
    senderId,
    content: { type: "text", text },
    status: "read",
    reactions: [],
    isPinned: false,
    isEdited: false,
    isDeleted: false,
    createdAt: createdAt.toISOString(),
    updatedAt: createdAt.toISOString(),
  };
}

function createMockMediaMessage(
  id: string,
  conversationId: string,
  senderId: string,
  content: MessageContent,
  createdAt: Date
): Message {
  return {
    id,
    conversationId,
    senderId,
    content,
    status: "read",
    reactions: [],
    isPinned: false,
    isEdited: false,
    isDeleted: false,
    createdAt: createdAt.toISOString(),
    updatedAt: createdAt.toISOString(),
  };
}

function generateSeedData(): {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
} {
  const now = Date.now();
  const min = 60_000;
  const hr = 3_600_000;

  // DM with Alice
  const aliceDmId = "conv-alice";
  const aliceMessages: Message[] = [
    createMockMessage("msg-a1", aliceDmId, "user-alice", "Hey! How's the messenger app coming along?", new Date(now - 2 * hr)),
    createMockMessage("msg-a2", aliceDmId, DEV_USER_ID, "Making good progress! Just got the foundation done.", new Date(now - 2 * hr + 5 * min)),
    createMockMessage("msg-a3", aliceDmId, "user-alice", "Nice! Can't wait to try it out", new Date(now - 2 * hr + 8 * min)),
    {
      ...createMockMessage("msg-a4", aliceDmId, DEV_USER_ID, "We now have rich text support!", new Date(now - hr + 30 * min)),
      content: {
        type: "text",
        text: "We now have rich text support!",
        formatting: [
          { start: 12, end: 21, format: "bold" },
        ],
      },
    },
    createMockMessage("msg-a5", aliceDmId, "user-alice", "Sounds great! Let me know when it's ready 🎉", new Date(now - 45 * min)),
    {
      ...createMockMessage("msg-a6", aliceDmId, DEV_USER_ID, "Check out bold, italic, and code formatting!", new Date(now - 40 * min)),
      content: {
        type: "text",
        text: "Check out bold, italic, and code formatting!",
        formatting: [
          { start: 10, end: 14, format: "bold" },
          { start: 16, end: 22, format: "italic" },
          { start: 28, end: 32, format: "code" },
        ],
      },
    },
    createMockMediaMessage("msg-a7", aliceDmId, "user-alice", {
      type: "image",
      url: "https://picsum.photos/seed/slumbr-chat/800/600",
      thumbnailUrl: "https://picsum.photos/seed/slumbr-chat/400/300",
      width: 800,
      height: 600,
      altText: "Screenshot of the new design",
    }, new Date(now - 35 * min)),
    createMockMessage("msg-a8", aliceDmId, "user-alice", "Here's the screenshot of the new design!", new Date(now - 34 * min)),
    createMockMediaMessage("msg-a9", aliceDmId, DEV_USER_ID, {
      type: "gif",
      url: "https://media.tenor.com/images/reaction-thumbs-up.gif",
      thumbnailUrl: "https://media.tenor.com/images/reaction-thumbs-up-sm.gif",
      width: 480,
      height: 270,
      source: "tenor",
    }, new Date(now - 30 * min)),
  ];

  // DM with Bob
  const bobDmId = "conv-bob";
  const bobMessages: Message[] = [
    createMockMessage("msg-b1", bobDmId, "user-bob", "Did you see the new design specs?", new Date(now - 5 * hr)),
    createMockMessage("msg-b2", bobDmId, DEV_USER_ID, "Yeah, they look awesome", new Date(now - 5 * hr + 3 * min)),
    createMockMessage("msg-b3", bobDmId, "user-bob", "I'll start working on the backend API tomorrow", new Date(now - 5 * hr + 10 * min)),
  ];

  // Group: Project Team
  const teamGroupId = "conv-team";
  const teamMessages: Message[] = [
    createMockMessage("msg-t1", teamGroupId, "user-carol", "Team standup in 10 minutes!", new Date(now - 3 * hr)),
    createMockMessage("msg-t2", teamGroupId, "user-alice", "On my way", new Date(now - 3 * hr + 2 * min)),
    createMockMessage("msg-t3", teamGroupId, DEV_USER_ID, "I'll share my screen for the demo", new Date(now - 3 * hr + 4 * min)),
    createMockMessage("msg-t4", teamGroupId, "user-bob", "Sounds good!", new Date(now - 3 * hr + 5 * min)),
    createMockMessage("msg-t5", teamGroupId, "user-carol", "Great standup everyone. Let's keep the momentum going!", new Date(now - 2 * hr - 30 * min)),
    createMockMessage("msg-t6", teamGroupId, "user-dave", "Agreed! I'll push my PR this afternoon", new Date(now - 2 * hr - 25 * min)),
    createMockMediaMessage("msg-t7", teamGroupId, "user-alice", {
      type: "image",
      url: "https://picsum.photos/seed/slumbr-wireframe/1200/800",
      thumbnailUrl: "https://picsum.photos/seed/slumbr-wireframe/600/400",
      width: 1200,
      height: 800,
      altText: "Updated wireframe for messenger",
    }, new Date(now - 2 * hr - 20 * min)),
    createMockMessage("msg-t8", teamGroupId, "user-alice", "Updated wireframe for the messenger ^", new Date(now - 2 * hr - 19 * min)),
  ];

  // Group: Design Review
  const designGroupId = "conv-design";
  const designMessages: Message[] = [
    createMockMessage("msg-d1", designGroupId, "user-eve", "I've uploaded the new mockups to Figma", new Date(now - 24 * hr)),
    createMockMessage("msg-d2", designGroupId, "user-alice", "The chat bubbles look really polished", new Date(now - 23 * hr)),
    createMockMessage("msg-d3", designGroupId, DEV_USER_ID, "Love the dark mode variant!", new Date(now - 22 * hr)),
    createMockMediaMessage("msg-d4", designGroupId, "user-eve", {
      type: "image",
      url: "https://picsum.photos/seed/slumbr-darkmode/1080/1920",
      thumbnailUrl: "https://picsum.photos/seed/slumbr-darkmode/540/960",
      width: 1080,
      height: 1920,
      altText: "Dark mode mockup — mobile view",
    }, new Date(now - 21 * hr)),
    createMockMessage("msg-d5", designGroupId, "user-eve", "Here's the dark mode mobile view 🌙", new Date(now - 21 * hr + min)),
  ];

  // DM with Carol (unread)
  const carolDmId = "conv-carol";
  const carolMessages: Message[] = [
    createMockMessage("msg-c1", carolDmId, "user-carol", "Hey, quick question about the encryption implementation", new Date(now - 15 * min)),
    createMockMessage("msg-c2", carolDmId, "user-carol", "Are we using Web Crypto API or a library?", new Date(now - 14 * min)),
  ];

  const conversations: Conversation[] = [
    {
      id: carolDmId,
      type: "direct",
      participants: [MOCK_PARTICIPANTS.self, MOCK_PARTICIPANTS.carol],
      lastMessage: carolMessages[carolMessages.length - 1],
      unreadCount: 2,
      isPinned: false,
      isMuted: false,
      isArchived: false,
      createdAt: new Date(now - 86400000 * 14).toISOString(),
      updatedAt: new Date(now - 14 * min).toISOString(),
    },
    {
      id: aliceDmId,
      type: "direct",
      participants: [MOCK_PARTICIPANTS.self, MOCK_PARTICIPANTS.alice],
      lastMessage: aliceMessages[aliceMessages.length - 1],
      unreadCount: 0,
      isPinned: true,
      isMuted: false,
      isArchived: false,
      createdAt: new Date(now - 86400000 * 30).toISOString(),
      updatedAt: new Date(now - 30 * min).toISOString(),
      theme: {
        accentColor: "#0D9488",
        bubbleStyle: "rounded",
        layoutMode: "bubbles",
        backgroundType: "gradient",
        backgroundGradient: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
      },
    },
    {
      id: teamGroupId,
      type: "group",
      name: "Project Team",
      description: "Slumbr project team — standups, demos, and coordination",
      participants: [
        MOCK_PARTICIPANTS.self,
        MOCK_PARTICIPANTS.alice,
        MOCK_PARTICIPANTS.bob,
        MOCK_PARTICIPANTS.carol,
        MOCK_PARTICIPANTS.dave,
      ],
      lastMessage: teamMessages[teamMessages.length - 1],
      unreadCount: 0,
      isPinned: true,
      isMuted: false,
      isArchived: false,
      createdAt: new Date(now - 86400000 * 60).toISOString(),
      updatedAt: new Date(now - 2 * hr - 19 * min).toISOString(),
    },
    {
      id: bobDmId,
      type: "direct",
      participants: [MOCK_PARTICIPANTS.self, MOCK_PARTICIPANTS.bob],
      lastMessage: bobMessages[bobMessages.length - 1],
      unreadCount: 0,
      isPinned: false,
      isMuted: false,
      isArchived: false,
      createdAt: new Date(now - 86400000 * 20).toISOString(),
      updatedAt: new Date(now - 5 * hr + 10 * min).toISOString(),
    },
    {
      id: designGroupId,
      type: "group",
      name: "Design Review",
      description: "Share mockups, give feedback, discuss UI/UX",
      participants: [
        MOCK_PARTICIPANTS.self,
        MOCK_PARTICIPANTS.alice,
        MOCK_PARTICIPANTS.eve,
      ],
      lastMessage: designMessages[designMessages.length - 1],
      unreadCount: 0,
      isPinned: false,
      isMuted: false,
      isArchived: false,
      createdAt: new Date(now - 86400000 * 45).toISOString(),
      updatedAt: new Date(now - 21 * hr + min).toISOString(),
    },
  ];

  const messages: Record<string, Message[]> = {
    [aliceDmId]: aliceMessages,
    [bobDmId]: bobMessages,
    [teamGroupId]: teamMessages,
    [designGroupId]: designMessages,
    [carolDmId]: carolMessages,
  };

  return { conversations, messages };
}

// ─── Known contacts (for add-member / create-group picker) ──────────────────

export interface KnownContact {
  userId: string;
  displayName: string;
  avatarUrl?: string;
}

export const KNOWN_CONTACTS: KnownContact[] = [
  { userId: "user-alice", displayName: "Alice Chen" },
  { userId: "user-bob", displayName: "Bob Martinez" },
  { userId: "user-carol", displayName: "Carol Kim" },
  { userId: "user-dave", displayName: "Dave Wilson" },
  { userId: "user-eve", displayName: "Eve Parker" },
  { userId: "user-frank", displayName: "Frank Liu" },
  { userId: "user-grace", displayName: "Grace Hopper" },
  { userId: "user-henry", displayName: "Henry Adams" },
];

// ─── Store Interface ─────────────────────────────────────────────────────────

/**
 * Per-conversation pagination state for the cursor-paginated message API.
 * `hasMore` flips to false the first time the server tells us there are
 * no older pages. `serverCursor` is the opaque `nextCursor` from the last
 * older-page fetch. `loading` guards against concurrent fetches firing
 * from rapid scroll-to-top.
 */
export interface ConversationPaging {
  hasMore: boolean;
  serverCursor: string | null;
  loading: boolean;
}

interface MessengerStore {
  conversations: Conversation[];
  messages: Record<string, Message[]>; // keyed by conversationId
  messagesPaging: Record<string, ConversationPaging>;
  /** Conversations that have been hydrated from IDB or fetched fresh at
   * least once this session. Used to avoid re-running `hydrateConversation`
   * needlessly when the user navigates back to a tab they already opened. */
  hydratedConversationIds: Set<string>;
  activeConversationId: string | null;
  searchQuery: string;
  typingIndicators: TypingIndicator[];
  isLoading: boolean;
  error: string | null;
  messageErrors: Record<string, string | null>;
  messageLoading: Record<string, boolean>;

  // ─── Emoji preferences (Phase 3) ────────────────────────────────────────
  emojiSet: EmojiSet;
  customEmojiPacks: CustomEmojiPack[];

  // ─── Conversation actions ────────────────────────────────────────────────
  fetchConversations: () => Promise<void>;
  /** Refetch the conversation list + the active conversation's latest page.
   * Called after realtime reconnects, when the tab becomes visible/online
   * again, and by the retry button — anything missed while disconnected is
   * healed here. Debounced internally (min 5s between network syncs). */
  resync: (opts?: { force?: boolean }) => Promise<void>;
  /** Boot-time hydration: paint conversations + recent messages from IDB
   * before the network resolves. Safe to call multiple times. */
  hydrateFromCache: () => Promise<void>;
  /** Ensure a conversation has its recent page loaded. Reads IDB first
   * (instant paint) then refreshes from the server. */
  hydrateConversation: (conversationId: string) => Promise<void>;
  /** Fetch the next older page of messages for a conversation. The
   * VirtualMessageList calls this when the user scrolls near the top. */
  loadOlderMessages: (conversationId: string) => Promise<void>;
  createConversation: (
    participantIds: string[],
    opts?: {
      name?: string;
      description?: string;
      isGroup?: boolean;
      contacts?: KnownContact[];
    }
  ) => string;
  deleteConversation: (id: string) => void;
  archiveConversation: (id: string) => void;
  unarchiveConversation: (id: string) => void;
  pinConversation: (id: string) => void;
  unpinConversation: (id: string) => void;
  muteConversation: (id: string) => void;
  unmuteConversation: (id: string) => void;
  setActiveConversation: (id: string | null) => void;
  markAsRead: (id: string) => void;
  setSearchQuery: (query: string) => void;
  syncConversationsFromRemote: (convs: Conversation[]) => void;
  updateMessagesInPlace: (conversationId: string, newMsgs: Message[]) => void;

  // ─── Group management (Phase 5) ─────────────────────────────────────────
  addMember: (conversationId: string, userId: string, displayName: string) => void;
  removeMember: (conversationId: string, userId: string) => void;
  promoteMember: (conversationId: string, userId: string) => void;
  demoteMember: (conversationId: string, userId: string) => void;
  updateGroupInfo: (conversationId: string, updates: { name?: string; description?: string; avatarUrl?: string }) => void;
  updateConversationAvatar: (conversationId: string, avatarUrl?: string) => void;
  leaveGroup: (conversationId: string) => void;

  // ─── Chat customization (Phase 6) ──────────────────────────────────────
  updateConversationTheme: (conversationId: string, theme: ChatTheme | undefined) => void;
  /** Set the disappearing-message TTL on a conversation. Pass 0 (or undefined)
   * to disable. Backend stamps every new message with a DDB TTL attribute. */
  updateConversationDisappearTtl: (conversationId: string, seconds: number) => void;

  // ─── Encryption (Phase 7) ───────────────────────────────────────────────
  encryptionStates: Record<string, ConversationEncryptionState>; // keyed by conversationId
  encryptionSettings: EncryptionSettings;
  isMessengerLocked: boolean;

  // Encryption actions
  initializeEncryptionKeys: () => Promise<void>;
  enableEncryption: (conversationId: string) => Promise<void>;
  disableEncryption: (conversationId: string) => void;
  verifySession: (conversationId: string) => void;
  getEncryptionDisplayInfo: (conversationId: string) => Promise<EncryptionDisplayInfo>;
  encryptAndSendMessage: (
    conversationId: string,
    content: MessageContent,
    options?: { replyToId?: string }
  ) => Promise<string>;
  decryptMessageContent: (conversationId: string, messageId: string) => Promise<string | null>;

  // PIN lock actions
  setPinLock: (pin: string) => Promise<void>;
  removePinLock: () => void;
  verifyPin: (pin: string) => Promise<boolean>;
  lockMessenger: () => void;
  unlockMessenger: (pin: string) => Promise<boolean>;
  updatePinLockConfig: (updates: Partial<PinLockConfig>) => void;

  // Encryption key management
  clearAllEncryptionData: () => Promise<void>;
  rotateKeys: () => Promise<void>;

  // ─── Message actions ─────────────────────────────────────────────────────
  sendMessage: (
    conversationId: string,
    content: MessageContent,
    options?: { replyToId?: string }
  ) => string;
  queueLocalMessage: (message: Message) => void;
  updateLocalMessage: (
    conversationId: string,
    messageId: string,
    updates: Partial<Message>
  ) => void;
  publishLocalMessage: (conversationId: string, message: Message) => void;
  editMessage: (
    conversationId: string,
    messageId: string,
    content: MessageContent
  ) => void;
  deleteMessage: (conversationId: string, messageId: string) => void;
  addReaction: (
    conversationId: string,
    messageId: string,
    emoji: string
  ) => void;
  removeReaction: (
    conversationId: string,
    messageId: string,
    emoji: string
  ) => void;
  pinMessage: (conversationId: string, messageId: string) => void;
  unpinMessage: (conversationId: string, messageId: string) => void;

  // ─── Typing indicators ──────────────────────────────────────────────────
  setTyping: (conversationId: string, userId: string, displayName: string) => void;
  clearTyping: (conversationId: string, userId: string) => void;

  // ─── Emoji preferences (Phase 3) ────────────────────────────────────────
  setEmojiSet: (set: EmojiSet) => void;
  addCustomEmojiPack: (pack: CustomEmojiPack) => void;
  removeCustomEmojiPack: (packId: string) => void;

  // ─── Huddle01 Calls (Phase 8) ──────────────────────────────────────────
  activeCall: CallSession | null;
  incomingCall: {
    roomId: string;
    conversationId: string;
    type: CallType;
    callerId: string;
    callerName: string;
  } | null;
  localCallState: LocalCallState;
  remoteParticipants: CallParticipant[];
  callHistory: CallSession[];
  callError: string | null;
  isCallMinimized: boolean;

  // Call actions
  startCall: (conversationId: string, type: CallType) => Promise<void>;
  acceptIncomingCall: () => Promise<void>;
  declineIncomingCall: () => void;
  endCall: () => void;
  setCallError: (error: string | null) => void;
  toggleMic: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
  updateRemoteParticipant: (peerId: string, updates: Partial<CallParticipant>) => void;
  addRemoteParticipant: (participant: CallParticipant) => void;
  removeRemoteParticipant: (peerId: string) => void;
  setCallStatus: (status: CallSession["status"]) => void;
  minimizeCall: () => void;
  maximizeCall: () => void;

  // ─── Computed ────────────────────────────────────────────────────────────
  getActiveConversation: () => Conversation | undefined;
  getConversationMessages: (id: string) => Message[];
  getFilteredConversations: () => Conversation[];
  getUnreadCount: () => number;
  getConversationDisplayName: (conversation: Conversation) => string;
  getTypingUsers: (conversationId: string) => TypingIndicator[];
}

// ─── Helper: persist state ───────────────────────────────────────────────────

function persist(state: { conversations: Conversation[]; messages: Record<string, Message[]> }) {
  if (DEV_AUTH_BYPASS) {
    saveToStorage(STORAGE_KEYS.CONVERSATIONS, state.conversations);
    saveToStorage(STORAGE_KEYS.MESSAGES, state.messages);
  }
  scheduleIdbPersist(state);
}

// ─── Initial state (loads from localStorage or seeds) ────────────────────────

function getInitialState(): {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
} {
  if (!DEV_AUTH_BYPASS) {
    return { conversations: [], messages: {} };
  }

  const stored = {
    conversations: loadFromStorage<Conversation[]>(STORAGE_KEYS.CONVERSATIONS, []),
    messages: loadFromStorage<Record<string, Message[]>>(STORAGE_KEYS.MESSAGES, {}),
  };

  // If we have persisted data, use it
  if (stored.conversations.length > 0) {
    return stored;
  }

  const seed = generateSeedData();
  persist(seed);
  return seed;
}

// ─── Store ───────────────────────────────────────────────────────────────────

const initial = getInitialState();

/**
 * Merge participant lists without letting an incoming payload downgrade the
 * profile fields we already hold.
 *
 * Membership (who is in the conversation, and their role) always comes from
 * the incoming payload — that's what the event is about. But `displayName`,
 * `avatarUrl` and `chatColor` are *snapshots*: a conversation row carries
 * whatever the person looked like when the row was last written. Only the
 * enriched read endpoints refresh them. Taking the incoming copy wholesale is
 * what kept resurrecting people's long-replaced profile pictures, so we keep
 * a locally-known value whenever the incoming one is missing or is the same
 * field going backwards to a blank.
 */
function mergeParticipants(
  local: ChatParticipant[] | undefined,
  incoming: ChatParticipant[] | undefined
): ChatParticipant[] {
  if (!incoming) return local ?? [];
  if (!local || local.length === 0) return incoming;
  const localById = new Map(local.map((p) => [p.userId, p]));
  return incoming.map((p) => {
    const known = localById.get(p.userId);
    if (!known) return p;
    return {
      ...p,
      displayName: p.displayName || known.displayName,
      avatarUrl: p.avatarUrl,
      chatColor: p.chatColor,
    };
  });
}

export const useMessengerStore = create<MessengerStore>((set, get) => ({
  conversations: initial.conversations,
  messages: initial.messages,
  messagesPaging: {},
  hydratedConversationIds: new Set<string>(),
  activeConversationId: null,
  searchQuery: "",
  typingIndicators: [],
  isLoading: false,
  error: null,
  messageErrors: {},
  messageLoading: {},

  // ─── Emoji preferences (Phase 3) ──────────────────────────────────────────
  emojiSet: (loadFromStorage<EmojiSet>("slumbr:messenger:emojiSet", "native")),
  customEmojiPacks: loadFromStorage<CustomEmojiPack[]>("slumbr:messenger:customEmojiPacks", []),

  // ─── Encryption state (Phase 7) ────────────────────────────────────────────
  encryptionStates: loadFromStorage<Record<string, ConversationEncryptionState>>(
    STORAGE_KEYS.ENCRYPTION_STATES,
    {}
  ),
  encryptionSettings: loadFromStorage<EncryptionSettings>(
    STORAGE_KEYS.ENCRYPTION_SETTINGS,
    DEFAULT_ENCRYPTION_SETTINGS
  ),
  isMessengerLocked: false, // starts unlocked — lock on tab visibility change if configured

  // ─── Huddle01 Calls (Phase 8) ──────────────────────────────────────────────
  activeCall: null,
  incomingCall: null,
  localCallState: {
    isMicOn: true,
    isCameraOn: false,
    isScreenSharing: false,
  },
  remoteParticipants: [],
  callHistory: loadFromStorage<CallSession[]>("slumbr:messenger:callHistory", []),
  callError: null,
  isCallMinimized: false,

  // ─── Conversation actions ──────────────────────────────────────────────────

  fetchConversations: async () => {
    if (DEV_AUTH_BYPASS) {
      set({ isLoading: false, error: null });
      return;
    }
    // Dedupe concurrent callers (mount effect, realtime resync, feed
    // prefetch can overlap) — they all await the same network attempt.
    if (inFlightConversationsFetch) return inFlightConversationsFetch;

    const epoch = messengerResetEpoch;
    const attempt = (async () => {
      set({ isLoading: true, error: null });
      // Lightweight: just the conversation list + lastMessage previews.
      // Per-conversation message history is loaded lazily via
      // `hydrateConversation` when the user opens a chat, so cold-start
      // payload stays small even for users with hundreds of conversations.
      //
      // Retry transient failures with backoff. A single failed attempt on a
      // fresh device used to leave the list empty until a manual reload —
      // this fetch is the only source of truth on cold start, so it must be
      // stubborn.
      const delays = [0, 1_500, 5_000];
      let lastErr: unknown = null;
      for (const delay of delays) {
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        if (epoch !== messengerResetEpoch) return;
        try {
          const fetchStartedAt = Date.now();
          const before = new Map(get().conversations.map((c) => [c.id, c]));
          const resp = await listChatConversations();
          if (epoch !== messengerResetEpoch) return; // profile switched mid-fetch
          const serverConvs = (resp.items as Conversation[]) ?? [];
          set((s) => {
            // Reconcile instead of wholesale-replace: the server list is
            // authoritative (rows it no longer returns are dropped — deleted
            // or soft-deleted elsewhere), EXCEPT conversations that appeared
            // locally via realtime moments ago and may not be readable in
            // this (eventually-consistent) fetch yet.
            const serverIds = new Set(serverConvs.map((c) => c.id));
            const keptLocal = s.conversations.filter(
              (c) =>
                !serverIds.has(c.id) &&
                msTime(c.updatedAt) > fetchStartedAt - 30_000
            );
            const current = new Map(s.conversations.map((c) => [c.id, c]));
            const conversations = [...serverConvs.map((remote) => {
              const local = current.get(remote.id);
              // A realtime event, read marker or optimistic send landed while fetching.
              if (local && local !== before.get(remote.id)) {
                return { ...remote, ...local, participants: remote.participants };
              }
              return remote;
            }), ...keptLocal];
            return {
              conversations,
              // Preserve any messages already in memory (from IDB hydration
              // or realtime). We no longer fetch them in this call.
              messages: s.messages,
              isLoading: false,
              error: null,
            };
          });
          lastConversationsSyncAt = Date.now();
          scheduleIdbPersist({
            conversations: get().conversations,
            messages: get().messages,
          });
          return;
        } catch (err) {
          lastErr = err;
          if (epoch !== messengerResetEpoch) return;
        }
      }
      if (epoch !== messengerResetEpoch) return;
      set({
        isLoading: false,
        error:
          lastErr instanceof Error ? lastErr.message : "Failed to fetch conversations",
      });
    })();

    const tracked = attempt.finally(() => {
      if (inFlightConversationsFetch === tracked) inFlightConversationsFetch = null;
    });
    inFlightConversationsFetch = tracked;
    return tracked;
  },

  resync: async (opts) => {
    if (DEV_AUTH_BYPASS) return;
    const now = Date.now();
    if (!opts?.force && now - lastConversationsSyncAt < 5_000) return;
    const activeId = get().activeConversationId;
    await Promise.all([get().fetchConversations(), activeId ? get().hydrateConversation(activeId) : Promise.resolve()]);
  },

  hydrateFromCache: async () => {
    if (DEV_AUTH_BYPASS) return;
    const epoch = messengerResetEpoch;
    try {
      const cachedConvs = await getCachedConversations();
      if (epoch !== messengerResetEpoch || cachedConvs.length === 0) return;

      // Merge with whatever the store already has so we never clobber
      // optimistic / realtime messages that arrived during hydration.
      // If a NETWORK fetch already succeeded this session, the server list
      // is authoritative — appending cached rows would resurrect
      // conversations deleted on another device.
      set((s) => {
        const networkAlreadySynced = lastConversationsSyncAt > 0;
        const existingConvIds = new Set(s.conversations.map((c) => c.id));
        const mergedConvs = networkAlreadySynced
          ? s.conversations
          : s.conversations.length === 0
            ? cachedConvs
            : [
                ...s.conversations,
                ...cachedConvs.filter((c) => !existingConvIds.has(c.id)),
              ];
        return { conversations: mergedConvs };
      });
    } catch (err) {
      console.warn("hydrateFromCache failed:", err);
    }
  },

  hydrateConversation: async (conversationId) => {
    if (DEV_AUTH_BYPASS) return;
    const inFlight = historyFetches.get(conversationId);
    if (inFlight) return inFlight;
    const epoch = messengerResetEpoch;
    const attempt = (async () => {
    const state = get();
    const alreadyHydrated = state.hydratedConversationIds.has(conversationId);
    state.hydratedConversationIds.add(conversationId);
    set((s) => ({ messageLoading: { ...s.messageLoading, [conversationId]: true }, messageErrors: { ...s.messageErrors, [conversationId]: null } }));

    // Step 1 — IDB paint. If the cache has anything we haven't loaded yet,
    // splice it in immediately so the UI has content to render.
    if (!alreadyHydrated) {
      try {
        const cached = await getCachedRecentMessages(conversationId, 50);
        if (epoch !== messengerResetEpoch) return;
        if (cached.length > 0) {
          set((s) => {
            const existing = s.messages[conversationId] ?? [];
            const byId = new Map(existing.map((m) => [m.id, m]));
            for (const m of cached) {
              if (!byId.has(m.id)) byId.set(m.id, m);
            }
            const merged = [...byId.values()].sort(
              (a, b) => msTime(a.createdAt) - msTime(b.createdAt)
            );
            return {
              messages: { ...s.messages, [conversationId]: merged },
            };
          });
        }
      } catch {
        // Cache miss / IDB unavailable — fall through to server fetch.
      }
    }

    // Step 2 — server refresh. Always hits the network so the user is
    // guaranteed to see the freshest messages (no stale state).
    try {
      await pendingConversationCreates.get(conversationId);
      if (epoch !== messengerResetEpoch) return;
      const meta = await getConversationMeta(conversationId);
      if (epoch !== messengerResetEpoch) return;
      const resp = await listChatMessages(conversationId, { limit: 50 });
      if (epoch !== messengerResetEpoch) return;
      const items = (resp.items as Message[]) ?? [];
      // Message timestamps describe history, not the current server clock.
      if (resp.serverTime) recordServerTimestamp(resp.serverTime);

      set((s) => {
        const existing = s.messages[conversationId] ?? [];
        const merged = mergeMessages(existing, items);
        // Don't reset the pagination cursor to page 1 if the user already
        // paged deeper — that caused chains of duplicate refetches on every
        // conversation re-open.
        const prevPaging = s.messagesPaging[conversationId];
        const paging: ConversationPaging = prevPaging
          ? prevPaging
          : {
              hasMore: resp.hasMore ?? !!resp.nextCursor,
              serverCursor: resp.nextCursor ?? null,
              loading: false,
            };
        return {
          messages: { ...s.messages, [conversationId]: merged },
          messagesPaging: {
            ...s.messagesPaging,
            [conversationId]: paging,
          },
        };
      });

      // Mirror to IDB + remember server cursor for next-older fetches.
      void putCachedMessages(conversationId, get().messages[conversationId] ?? []);
      void setConversationMeta(conversationId, {
        serverCursor: resp.nextCursor ?? meta.serverCursor ?? null,
        hasMoreOnServer: resp.hasMore ?? !!resp.nextCursor,
        lastSyncedAt: Date.now(),
      });
    } catch (err) {
      if (epoch !== messengerResetEpoch) return;
      console.warn("hydrateConversation refresh failed:", err);
      get().hydratedConversationIds.delete(conversationId);
      set((s) => ({ messageErrors: { ...s.messageErrors, [conversationId]: "Couldn't refresh messages. Try again." } }));
    } finally {
      if (epoch === messengerResetEpoch) set((s) => ({ messageLoading: { ...s.messageLoading, [conversationId]: false } }));
    }
    })();
    historyFetches.set(conversationId, attempt);
    try { await attempt; } finally {
      if (historyFetches.get(conversationId) === attempt) historyFetches.delete(conversationId);
    }
  },

  loadOlderMessages: async (conversationId) => {
    const epoch = messengerResetEpoch;
    const state = get();
    const paging = state.messagesPaging[conversationId];
    if (paging?.loading) return;
    // If we never hydrated, hydrating will load the first page — older
    // pages only make sense afterwards.
    if (!paging) {
      await get().hydrateConversation(conversationId);
      return;
    }
    if (!paging.hasMore) return;

    set((s) => ({
      messagesPaging: {
        ...s.messagesPaging,
        [conversationId]: { ...paging, loading: true },
      },
    }));

    try {
      const resp = await listChatMessages(conversationId, {
        before: paging.serverCursor ?? undefined,
        limit: 50,
      });
      if (epoch !== messengerResetEpoch) return;
      const items = (resp.items as Message[]) ?? [];

      set((s) => {
        const existing = s.messages[conversationId] ?? [];
        const merged = mergeMessages(existing, items);
        const newPaging: ConversationPaging = {
          hasMore: resp.hasMore ?? !!resp.nextCursor,
          serverCursor: resp.nextCursor ?? null,
          loading: false,
        };
        return {
          messages: { ...s.messages, [conversationId]: merged },
          messagesPaging: {
            ...s.messagesPaging,
            [conversationId]: newPaging,
          },
        };
      });

      void putCachedMessages(conversationId, items);
      void setConversationMeta(conversationId, {
        serverCursor: resp.nextCursor ?? null,
        hasMoreOnServer: resp.hasMore ?? !!resp.nextCursor,
      });
    } catch (err) {
      if (epoch !== messengerResetEpoch) return;
      console.warn("loadOlderMessages failed:", err);
      set((s) => ({ messageErrors: { ...s.messageErrors, [conversationId]: "Couldn't load older messages. Try again." } }));
      set((s) => {
        const current = s.messagesPaging[conversationId];
        if (!current) return s;
        return {
          messagesPaging: {
            ...s.messagesPaging,
            [conversationId]: { ...current, loading: false },
          },
        };
      });
    }
  },

  createConversation: (participantIds, opts) => {
    const id = generateUUID();
    const now = new Date().toISOString();
    const contactLookup = new Map(
      [...KNOWN_CONTACTS, ...(opts?.contacts ?? [])].map((contact) => [
        contact.userId,
        contact,
      ])
    );

    const currentUserId = getCurrentUserId();
    const currentUserName = getCurrentUserName();
    const currentUserAvatar = getCurrentUserAvatar();

    const participants: ChatParticipant[] = [
      createMockParticipant(currentUserId, currentUserName, "owner", currentUserAvatar),
      ...participantIds.map((uid) => {
        const contact = contactLookup.get(uid);
        // Friend may have signed up but not set a displayName yet — show
        // "User abc123" instead of dumping the full UUID, and skip avatarUrl
        // entirely if it's empty string (browser would fetch "" and 404).
        const rawName = contact?.displayName?.trim();
        const fallback = `User ${uid.slice(0, 6)}`;
        const displayName = rawName && rawName.length > 0 ? rawName : fallback;
        const rawAvatar = contact?.avatarUrl?.trim();
        const avatar = rawAvatar && rawAvatar.length > 0 ? rawAvatar : undefined;
        return createMockParticipant(uid, displayName, "member", avatar);
      }),
    ];

    const conversation: Conversation = {
      id,
      type: opts?.isGroup ? "group" : "direct",
      name: opts?.name,
      description: opts?.description,
      participants,
      unreadCount: 0,
      isPinned: false,
      isMuted: false,
      isArchived: false,
      createdAt: now,
      updatedAt: now,
    };

    // Generate system message for group creation
    const initialMessages: Message[] = [];
    if (opts?.isGroup) {
      const systemMsg: Message = {
        id: generateUUID(),
        conversationId: id,
        senderId: currentUserId,
        content: {
          type: "system",
          text: `${currentUserName} created the group "${opts.name || "Group"}"`,
          action: "created_group",
        } satisfies SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };
      initialMessages.push(systemMsg);
      conversation.lastMessage = systemMsg;
    }

    set((s) => {
      const next = {
        conversations: [conversation, ...s.conversations],
        messages: { ...s.messages, [id]: initialMessages },
      };
      persist(next);
      return next;
    });

    // Synchronously hit the dedicated create endpoint so the conversation
    // exists in DDB before any subsequent send. The save-state debounce
    // (600ms) used to be the only path that created the row, which left a
    // window where `createMessage` would 404 if the user typed and hit
    // send the instant after creating a chat. Fire-and-forget — local
    // optimistic state already shows the conversation; the network call
    // just races save-state to plant the row.
    if (!DEV_AUTH_BYPASS) {
      const epoch = messengerResetEpoch;
      const creation = createChatConversation({
        id,
        type: conversation.type,
        ...(conversation.name ? { name: conversation.name } : {}),
        ...(conversation.description ? { description: conversation.description } : {}),
        participants: participants.map(serializeParticipant),
        unreadCount: 0,
        isPinned: false,
        isMuted: false,
        isArchived: false,
        initialMessages: initialMessages.map(stripClientMessageFields),
      });
      pendingConversationCreates.set(id, creation);
      void creation.then((response) => {
        if (epoch !== messengerResetEpoch) return;
        if (response && typeof response === "object" && "id" in response) get().syncConversationsFromRemote([response as Conversation]);
      }).catch((err) => {
        if (epoch !== messengerResetEpoch) return;
        console.warn("[Chat] createChatConversation failed:", err);
        set({ error: "Couldn't create the conversation. Please try again." });
      });
    }

    return id;
  },

  deleteConversation: (id) => {
    // Optimistic local removal — backend semantics are:
    //   - Group + caller is owner: hard delete (cascades media)
    //   - DM: soft-delete on caller's side (UserStateItem.deletedAt). The
    //     conversation reappears on the next inbound message.
    //   - Both DM parties have soft-deleted: backend promotes to hard delete.
    set((s) => {
      const conversations = s.conversations.filter((c) => c.id !== id);
      const messages = { ...s.messages };
      delete messages[id];
      persist({ conversations, messages });
      return {
        conversations,
        messages,
        activeConversationId:
          s.activeConversationId === id ? null : s.activeConversationId,
      };
    });
    if (!DEV_AUTH_BYPASS) {
      void deleteChatConversation(id).catch((err) =>
        console.warn("[Chat] deleteChatConversation failed:", err)
      );
    }
    // Drop the IDB mirror for this conversation so it doesn't repopulate
    // on next cold start.
    void deleteCachedConversation(id);
  },

  archiveConversation: (id) => {
    // Server-persisted per-user state — without this the flag silently
    // reverted on the next list fetch (and was device-local only).
    void updateChatConversationState(id, { isArchived: true }).catch((err) =>
      console.warn("[Chat] archiveConversation sync failed", err)
    );
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, isArchived: true, updatedAt: new Date().toISOString() } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  unarchiveConversation: (id) => {
    // Server-persisted per-user state — without this the flag silently
    // reverted on the next list fetch (and was device-local only).
    void updateChatConversationState(id, { isArchived: false }).catch((err) =>
      console.warn("[Chat] unarchiveConversation sync failed", err)
    );
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, isArchived: false, updatedAt: new Date().toISOString() } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  pinConversation: (id) => {
    // Server-persisted per-user state — without this the flag silently
    // reverted on the next list fetch (and was device-local only).
    void updateChatConversationState(id, { isPinned: true }).catch((err) =>
      console.warn("[Chat] pinConversation sync failed", err)
    );
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, isPinned: true } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  unpinConversation: (id) => {
    // Server-persisted per-user state — without this the flag silently
    // reverted on the next list fetch (and was device-local only).
    void updateChatConversationState(id, { isPinned: false }).catch((err) =>
      console.warn("[Chat] unpinConversation sync failed", err)
    );
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, isPinned: false } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  muteConversation: (id) => {
    // Server-persisted per-user state — without this the flag silently
    // reverted on the next list fetch (and was device-local only).
    void updateChatConversationState(id, { isMuted: true }).catch((err) =>
      console.warn("[Chat] muteConversation sync failed", err)
    );
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, isMuted: true } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  unmuteConversation: (id) => {
    // Server-persisted per-user state — without this the flag silently
    // reverted on the next list fetch (and was device-local only).
    void updateChatConversationState(id, { isMuted: false }).catch((err) =>
      console.warn("[Chat] unmuteConversation sync failed", err)
    );
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, isMuted: false } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  setActiveConversation: (id) => {
    set({ activeConversationId: id });
    if (id) {
      get().markAsRead(id);
    }
  },

  syncConversationsFromRemote: (remoteConvs: Conversation[]) => {
    set((state) => {
      const localById = new Map(state.conversations.map((c) => [c.id, c]));
      let changed = false;
      let conversations = state.conversations;

      for (const rc of remoteConvs) {
        if (!rc.participants.some((p) => p.userId === getCurrentUserId())) {
          conversations = conversations.filter((c) => c.id !== rc.id);
          changed = true;
          continue;
        }
        const local = localById.get(rc.id);
        if (!local) {
          conversations = [...conversations, rc];
          localById.set(rc.id, rc);
          changed = true;
        } else if (msTime(rc.updatedAt) >= msTime(local.updatedAt)) {
          const merged = {
            ...local,
            ...rc,
            isPinned: local.isPinned,
            isMuted: local.isMuted,
            isArchived: local.isArchived,
            unreadCount: local.unreadCount,
            lastMessage: local.lastMessage && (!rc.lastMessage || msTime(local.lastMessage.createdAt) > msTime(rc.lastMessage.createdAt)) ? local.lastMessage : rc.lastMessage,
            participants: mergeParticipants(local.participants, rc.participants),
          };
          conversations = conversations.map((c) => (c.id === rc.id ? merged : c));
          localById.set(rc.id, merged);
          changed = true;
        }
      }

      if (!changed) return state;
      scheduleIdbPersist({ conversations, messages: state.messages });
      return { conversations };
    });
  },

  updateMessagesInPlace: (conversationId: string, newMsgs: Message[]) => {
    set((state) => {
      const existing = state.messages[conversationId] ?? [];
      const merged = mergeMessages(existing, newMsgs);
      const messages = { ...state.messages, [conversationId]: merged };
      const lastMsg = [...merged].reverse().find((message) => isMessageVisible(message));
      const conversations = state.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        const deletedPreview = newMsgs.some((m) => m.id === c.lastMessage?.id && m.isDeleted);
        if (deletedPreview || (lastMsg && (!c.lastMessage || !isMessageVisible(c.lastMessage) || msTime(lastMsg.createdAt) >= msTime(c.lastMessage.createdAt)))) {
          return { ...c, lastMessage: lastMsg, updatedAt: Math.max(msTime(c.updatedAt), msTime(lastMsg?.createdAt)) };
        }
        return c;
      });

      scheduleIdbPersist({ conversations, messages });
      return { conversations, messages };
    });
  },

  markAsRead: (id) => {
    // Persist the read marker server-side so unread counts are correct on
    // every device (list-conversations computes them from lastReadAt).
    // Debounced per conversation — this fires on every incoming message
    // while the chat is open.
    const now = Date.now();
    const lastSent = lastMarkReadSentAt.get(id) ?? 0;
    if (!DEV_AUTH_BYPASS && now - lastSent >= 5_000) {
      lastMarkReadSentAt.set(id, now);
      void updateChatConversationState(id, { markRead: true }).catch((err) => {
        lastMarkReadSentAt.delete(id);
        console.warn("[Chat] markAsRead sync failed", err);
      });
    } else if (!DEV_AUTH_BYPASS && !readTimers.has(id)) {
      readTimers.set(id, setTimeout(() => {
        readTimers.delete(id);
        get().markAsRead(id);
      }, Math.max(1, 5_000 - (now - lastSent))));
    }
    set((s) => {
      const conversations = s.conversations.map((c) =>
        c.id === id ? { ...c, unreadCount: 0 } : c
      );
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
  },

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  // ─── Group management (Phase 5) ───────────────────────────────────────────

  addMember: (conversationId, userId, displayName) => {
    set((s) => {
      const now = new Date().toISOString();
      const newParticipant = createMockParticipant(userId, displayName, "member");

      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        if (c.participants.some((p) => p.userId === userId)) return c; // already a member
        return { ...c, participants: [...c.participants, newParticipant], updatedAt: now };
      });

      // Add system message
      const systemMsg: Message = {
        id: generateUUID(),
        conversationId,
        senderId: DEV_USER_ID,
        content: {
          type: "system",
          text: `${DEV_USER_NAME} added ${displayName}`,
          action: "added_member",
        } satisfies SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };

      const convMessages = [...(s.messages[conversationId] ?? []), systemMsg];
      const messages = { ...s.messages, [conversationId]: convMessages };

      // Update lastMessage
      const updatedConversations = conversations.map((c) =>
        c.id === conversationId ? { ...c, lastMessage: systemMsg } : c
      );

      persist({ conversations: updatedConversations, messages });
      return { conversations: updatedConversations, messages };
    });
    const conv = get().conversations.find((c) => c.id === conversationId);
    if (conv) {
      void updateChatConversation(conversationId, {
        participants: conv.participants.map(serializeParticipant),
      }).catch((err) => console.warn("[Chat] addMember sync failed", err));
    }
  },

  removeMember: (conversationId, userId) => {
    set((s) => {
      const now = new Date().toISOString();
      const conv = s.conversations.find((c) => c.id === conversationId);
      const removedParticipant = conv?.participants.find((p) => p.userId === userId);
      const removedName = removedParticipant?.displayName ?? userId;

      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        return {
          ...c,
          participants: c.participants.filter((p) => p.userId !== userId),
          updatedAt: now,
        };
      });

      // Add system message
      const systemMsg: Message = {
        id: generateUUID(),
        conversationId,
        senderId: DEV_USER_ID,
        content: {
          type: "system",
          text: `${DEV_USER_NAME} removed ${removedName}`,
          action: "removed_member",
        } satisfies SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };

      const convMessages = [...(s.messages[conversationId] ?? []), systemMsg];
      const messages = { ...s.messages, [conversationId]: convMessages };

      const updatedConversations = conversations.map((c) =>
        c.id === conversationId ? { ...c, lastMessage: systemMsg } : c
      );

      persist({ conversations: updatedConversations, messages });
      return { conversations: updatedConversations, messages };
    });
    const conv = get().conversations.find((c) => c.id === conversationId);
    if (conv && conv.participants.length > 0) {
      void updateChatConversation(conversationId, {
        participants: conv.participants.map(serializeParticipant),
      }).catch((err) => console.warn("[Chat] removeMember sync failed", err));
    }
  },

  promoteMember: (conversationId, userId) => {
    set((s) => {
      const now = new Date().toISOString();
      let promotedName = userId;

      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        return {
          ...c,
          participants: c.participants.map((p) => {
            if (p.userId === userId && p.role === "member") {
              promotedName = p.displayName;
              return { ...p, role: "admin" as const };
            }
            return p;
          }),
          updatedAt: now,
        };
      });

      // Add system message
      const systemMsg: Message = {
        id: generateUUID(),
        conversationId,
        senderId: DEV_USER_ID,
        content: {
          type: "system",
          text: `${DEV_USER_NAME} made ${promotedName} an admin`,
          action: "added_member", // reuse closest action type
        } satisfies SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };

      const convMessages = [...(s.messages[conversationId] ?? []), systemMsg];
      const messages = { ...s.messages, [conversationId]: convMessages };

      const updatedConversations = conversations.map((c) =>
        c.id === conversationId ? { ...c, lastMessage: systemMsg } : c
      );

      persist({ conversations: updatedConversations, messages });
      return { conversations: updatedConversations, messages };
    });
    const conv = get().conversations.find((c) => c.id === conversationId);
    if (conv) {
      void updateChatConversation(conversationId, {
        participants: conv.participants.map(serializeParticipant),
      }).catch((err) => console.warn("[Chat] promoteMember sync failed", err));
    }
  },

  demoteMember: (conversationId, userId) => {
    set((s) => {
      const now = new Date().toISOString();
      let demotedName = userId;

      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        return {
          ...c,
          participants: c.participants.map((p) => {
            if (p.userId === userId && p.role === "admin") {
              demotedName = p.displayName;
              return { ...p, role: "member" as const };
            }
            return p;
          }),
          updatedAt: now,
        };
      });

      // Add system message
      const systemMsg: Message = {
        id: generateUUID(),
        conversationId,
        senderId: DEV_USER_ID,
        content: {
          type: "system",
          text: `${DEV_USER_NAME} removed ${demotedName} as admin`,
          action: "removed_member", // reuse closest action type
        } satisfies SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };

      const convMessages = [...(s.messages[conversationId] ?? []), systemMsg];
      const messages = { ...s.messages, [conversationId]: convMessages };

      const updatedConversations = conversations.map((c) =>
        c.id === conversationId ? { ...c, lastMessage: systemMsg } : c
      );

      persist({ conversations: updatedConversations, messages });
      return { conversations: updatedConversations, messages };
    });
    const conv = get().conversations.find((c) => c.id === conversationId);
    if (conv) {
      void updateChatConversation(conversationId, {
        participants: conv.participants.map(serializeParticipant),
      }).catch((err) => console.warn("[Chat] demoteMember sync failed", err));
    }
  },

  updateGroupInfo: (conversationId, updates) => {
    set((s) => {
      const now = new Date().toISOString();
      const conv = s.conversations.find((c) => c.id === conversationId);

      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        return {
          ...c,
          ...(updates.name !== undefined && { name: updates.name }),
          ...(updates.description !== undefined && { description: updates.description }),
          ...(updates.avatarUrl !== undefined && { avatarUrl: updates.avatarUrl }),
          updatedAt: now,
        };
      });

      // Generate system message for name change
      const systemMessages: Message[] = [];
      if (updates.name !== undefined && updates.name !== conv?.name) {
        systemMessages.push({
          id: generateUUID(),
          conversationId,
          senderId: DEV_USER_ID,
          content: {
            type: "system",
            text: `${DEV_USER_NAME} changed the group name to "${updates.name}"`,
            action: "changed_name",
          } satisfies SystemContent,
          status: "read",
          reactions: [],
          isPinned: false,
          isEdited: false,
          isDeleted: false,
          createdAt: now,
          updatedAt: now,
        });
      }

      if (updates.avatarUrl !== undefined && updates.avatarUrl !== conv?.avatarUrl) {
        systemMessages.push({
          id: generateUUID(),
          conversationId,
          senderId: DEV_USER_ID,
          content: {
            type: "system",
            text: `${DEV_USER_NAME} changed the group avatar`,
            action: "changed_avatar",
          } satisfies SystemContent,
          status: "read",
          reactions: [],
          isPinned: false,
          isEdited: false,
          isDeleted: false,
          createdAt: now,
          updatedAt: now,
        });
      }

      const convMessages = [...(s.messages[conversationId] ?? []), ...systemMessages];
      const messages = { ...s.messages, [conversationId]: convMessages };

      const lastSystemMsg = systemMessages[systemMessages.length - 1];
      const updatedConversations = lastSystemMsg
        ? conversations.map((c) =>
            c.id === conversationId ? { ...c, lastMessage: lastSystemMsg } : c
          )
        : conversations;

      persist({ conversations: updatedConversations, messages });
      return { conversations: updatedConversations, messages };
    });
    const apiUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) apiUpdates.name = updates.name;
    if (updates.description !== undefined) apiUpdates.description = updates.description;
    if (updates.avatarUrl !== undefined) apiUpdates.avatarUrl = updates.avatarUrl;
    if (Object.keys(apiUpdates).length > 0) {
      void updateChatConversation(conversationId, apiUpdates).catch((err) =>
        console.warn("[Chat] groupInfo sync failed", err)
      );
    }
  },

  updateConversationAvatar: (conversationId, avatarUrl) => {
    const nextAvatarUrl = avatarUrl?.trim() || undefined;

    set((s) => {
      const now = new Date().toISOString();
      const conversations = s.conversations.map((conversation) => {
        if (conversation.id !== conversationId) return conversation;

        const { avatarUrl: _previousAvatarUrl, ...rest } = conversation;
        return nextAvatarUrl
          ? { ...rest, avatarUrl: nextAvatarUrl, updatedAt: now }
          : { ...rest, updatedAt: now };
      });

      persist({ conversations, messages: s.messages });
      return { conversations };
    });

    void updateChatConversation(conversationId, {
      avatarUrl: nextAvatarUrl ?? null,
    }).catch((err) =>
      console.warn("[Chat] conversation avatar sync failed", err)
    );
  },

  leaveGroup: (conversationId) => {
    const stateBefore = get();
    const conv = stateBefore.conversations.find((c) => c.id === conversationId);
    const myId = getCurrentUserId();
    set((s) => {
      const now = new Date().toISOString();

      // Add system message before leaving
      const systemMsg: Message = {
        id: generateUUID(),
        conversationId,
        senderId: DEV_USER_ID,
        content: {
          type: "system",
          text: `${DEV_USER_NAME} left the group`,
          action: "left_group",
        } satisfies SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };

      const convMessages = [...(s.messages[conversationId] ?? []), systemMsg];

      // Remove the conversation from the list (or mark archived)
      const conversations = s.conversations.filter((c) => c.id !== conversationId);
      const messages = { ...s.messages, [conversationId]: convMessages };

      persist({ conversations, messages });
      return {
        conversations,
        messages,
        activeConversationId:
          s.activeConversationId === conversationId ? null : s.activeConversationId,
      };
    });
    if (conv) {
      const remaining = conv.participants.filter((p) => p.userId !== myId);
      if (remaining.length > 0) {
        void updateChatConversation(conversationId, {
          participants: remaining.map(serializeParticipant),
        }).catch((err) => console.warn("[Chat] leaveGroup sync failed", err));
      }
    }
  },

  // ─── Chat customization (Phase 6) ────────────────────────────────────────

  updateConversationTheme: (conversationId, theme) => {
    set((s) => {
      const now = new Date().toISOString();

      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        return { ...c, theme, updatedAt: now };
      });

      persist({ conversations, messages: s.messages });
      return { conversations };
    });
    void updateChatConversation(conversationId, { theme: theme ?? null }).catch(
      (err) => console.warn("[Chat] theme sync failed", err)
    );
  },

  updateConversationDisappearTtl: (conversationId, seconds) => {
    set((s) => {
      const now = new Date().toISOString();
      const next = seconds > 0 ? seconds : undefined;
      const conversations = s.conversations.map((c) => {
        if (c.id !== conversationId) return c;
        return { ...c, disappearAfterSeconds: next, updatedAt: now };
      });
      persist({ conversations, messages: s.messages });
      return { conversations };
    });
    void updateChatConversation(conversationId, {
      disappearAfterSeconds: seconds > 0 ? seconds : null,
    }).catch((err) => console.warn("[Chat] TTL sync failed", err));
  },

  // ─── Message actions ───────────────────────────────────────────────────────

  sendMessage: (conversationId, content, options) => {
    const id = generateUUID();
    // Optimistic stamp uses approximate server-now (Date.now() shifted by
    // the last observed server clock offset) so a skewed local clock
    // doesn't park the message in the wrong slot before the round trip
    // completes. The authoritative values overwrite this on resolve.
    const now = getApproxServerNowMs();

    const message: Message = {
      id,
      conversationId,
      senderId: getCurrentUserId(),
      content,
      status: "sending",
      ...(options?.replyToId ? { replyToId: options.replyToId } : {}),
      reactions: [],
      isPinned: false,
      isEdited: false,
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
    };

    set((s) => {
      const convMessages = [...(s.messages[conversationId] ?? []), message];
      const messages = { ...s.messages, [conversationId]: convMessages };

      const conversations = s.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, lastMessage: message, updatedAt: now }
          : c
      );

      persist({ conversations, messages });
      return { conversations, messages };
    });

    publishMessage(conversationId, message);

    return id;
  },

  queueLocalMessage: (message) => {
    set((s) => {
      const existing = s.messages[message.conversationId] ?? [];
      const nextMessages = existing.some((m) => m.id === message.id)
        ? existing.map((m) => (m.id === message.id ? message : m))
        : [...existing, message];

      nextMessages.sort((a, b) => msTime(a.createdAt) - msTime(b.createdAt));

      const conversations = s.conversations.map((conversation) =>
        conversation.id === message.conversationId
          ? {
              ...conversation,
              lastMessage: message,
              updatedAt: message.createdAt,
            }
          : conversation
      );
      const messages = { ...s.messages, [message.conversationId]: nextMessages };
      scheduleIdbPersist({ conversations, messages });

      return {
        conversations,
        messages,
      };
    });
  },

  updateLocalMessage: (conversationId, messageId, updates) => {
    set((s) => {
      if (!s.messages[conversationId]?.some((message) => message.id === messageId)) return s;
      const convMessages = (s.messages[conversationId] ?? []).map((message) =>
        message.id === messageId ? { ...message, ...updates } : message
      );
      const updatedMessage = convMessages.find((message) => message.id === messageId);
      const conversations = updatedMessage
        ? s.conversations.map((conversation) =>
            conversation.id === conversationId &&
            conversation.lastMessage?.id === messageId
              ? { ...conversation, lastMessage: updatedMessage }
              : conversation
          )
        : s.conversations;
      const messages = { ...s.messages, [conversationId]: convMessages };
      scheduleIdbPersist({ conversations, messages });

      return {
        conversations,
        messages,
      };
    });
  },

  publishLocalMessage: (conversationId, message) => {
    if (pendingMessagePublishes.has(message.id)) return;
    const cleanMessage: Message = {
      ...message,
      status: "sending",
      uploadState: undefined,
      uploadError: undefined,
      updatedAt: getApproxServerNowMs(),
    };

    set((s) => {
      const convMessages = (s.messages[conversationId] ?? []).map((existing) =>
        existing.id === cleanMessage.id ? cleanMessage : existing
      );
      const messages = { ...s.messages, [conversationId]: convMessages };
      const conversations = s.conversations.map((conversation) =>
        conversation.id === conversationId && (!conversation.lastMessage || conversation.lastMessage.id === message.id)
          ? { ...conversation, lastMessage: cleanMessage, updatedAt: cleanMessage.createdAt }
          : conversation
      );
      persist({ conversations, messages });
      return { conversations, messages };
    });

    publishMessage(conversationId, cleanMessage);
  },

  editMessage: (conversationId, messageId, content) => {
    const previous = get().messages[conversationId]?.find((m) => m.id === messageId);
    set((s) => {
      const updatedAt = new Date().toISOString();
      const convMessages = (s.messages[conversationId] ?? []).map((m) =>
        m.id === messageId
          ? { ...m, content, isEdited: true, updatedAt }
          : m
      );
      const messages = { ...s.messages, [conversationId]: convMessages };

      // Update lastMessage if this was the last one
      const lastMsg = convMessages[convMessages.length - 1];
      const conversations = s.conversations.map((c) =>
        c.id === conversationId && c.lastMessage?.id === messageId
          ? { ...c, lastMessage: lastMsg }
          : c
      );

      persist({ conversations, messages });
      return { conversations, messages };
    });

    syncMessageUpdate(conversationId, messageId, {
      content,
      isEdited: true,
    }, previous);
  },

  deleteMessage: (conversationId, messageId) => {
    const previous = get().messages[conversationId]?.find((m) => m.id === messageId);
    const epoch = messengerResetEpoch;
    set((s) => {
      const convMessages = (s.messages[conversationId] ?? []).map((m) =>
        m.id === messageId
          ? { ...m, isDeleted: true, content: { type: "text" as const, text: "" }, encryptedEnvelope: undefined, updatedAt: new Date().toISOString() }
          : m
      );
      const messages = { ...s.messages, [conversationId]: convMessages };

      // Update lastMessage to most recent non-deleted
      const lastVisible = [...convMessages].reverse().find((m) => !m.isDeleted);
      const conversations = s.conversations.map((c) =>
        c.id === conversationId ? { ...c, lastMessage: lastVisible } : c
      );

      persist({ conversations, messages });
      return { conversations, messages };
    });
    // Soft-delete on backend so other clients see the deletion in real time
    if (!DEV_AUTH_BYPASS) void deleteChatMessage(conversationId, messageId).catch((err) => {
      if (epoch !== messengerResetEpoch || (err as { status?: number })?.status === 404) return;
      if (previous) get().updateLocalMessage(conversationId, messageId, previous);
      set((s) => ({ messageErrors: { ...s.messageErrors, [conversationId]: "Message couldn't be deleted. Please try again." } }));
      void get().fetchConversations();
    });
  },

  addReaction: (conversationId, messageId, emoji) => {
    const previous = get().messages[conversationId]?.find((m) => m.id === messageId);
    set((s) => {
      const currentUserId = getCurrentUserId();
      const convMessages = (s.messages[conversationId] ?? []).map((m) => {
        if (m.id !== messageId) return m;
        const existing = m.reactions.find((r) => r.emoji === emoji);
        if (existing) {
          if (existing.userIds.includes(currentUserId)) return m;
          return {
            ...m,
            reactions: m.reactions.map((r) =>
              r.emoji === emoji
                ? { ...r, userIds: [...r.userIds, currentUserId] }
                : r
            ),
          };
        }
        return {
          ...m,
          reactions: [...m.reactions, { emoji, userIds: [currentUserId] }],
        };
      });
      const messages = { ...s.messages, [conversationId]: convMessages };
      persist({ conversations: s.conversations, messages });
      return { messages };
    });
    const updated = get().messages[conversationId]?.find((m) => m.id === messageId);
    if (updated) {
      syncMessageUpdate(conversationId, messageId, {
        reactions: updated.reactions,
      }, previous);
    }
  },

  removeReaction: (conversationId, messageId, emoji) => {
    const previous = get().messages[conversationId]?.find((m) => m.id === messageId);
    set((s) => {
      const currentUserId = getCurrentUserId();
      const convMessages = (s.messages[conversationId] ?? []).map((m) => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          reactions: m.reactions
            .map((r) =>
              r.emoji === emoji
                ? { ...r, userIds: r.userIds.filter((uid) => uid !== currentUserId) }
                : r
            )
            .filter((r) => r.userIds.length > 0),
        };
      });
      const messages = { ...s.messages, [conversationId]: convMessages };
      persist({ conversations: s.conversations, messages });
      return { messages };
    });
    const updated = get().messages[conversationId]?.find((m) => m.id === messageId);
    if (updated) {
      syncMessageUpdate(conversationId, messageId, {
        reactions: updated.reactions,
      }, previous);
    }
  },

  pinMessage: (conversationId, messageId) => {
    const previous = get().messages[conversationId]?.find((m) => m.id === messageId);
    set((s) => {
      const convMessages = (s.messages[conversationId] ?? []).map((m) =>
        m.id === messageId ? { ...m, isPinned: true } : m
      );
      const messages = { ...s.messages, [conversationId]: convMessages };
      persist({ conversations: s.conversations, messages });
      return { messages };
    });
    syncMessageUpdate(conversationId, messageId, { isPinned: true }, previous);
  },

  unpinMessage: (conversationId, messageId) => {
    const previous = get().messages[conversationId]?.find((m) => m.id === messageId);
    set((s) => {
      const convMessages = (s.messages[conversationId] ?? []).map((m) =>
        m.id === messageId ? { ...m, isPinned: false } : m
      );
      const messages = { ...s.messages, [conversationId]: convMessages };
      persist({ conversations: s.conversations, messages });
      return { messages };
    });
    syncMessageUpdate(conversationId, messageId, { isPinned: false }, previous);
  },

  // ─── Typing indicators ────────────────────────────────────────────────────

  setTyping: (conversationId, userId, displayName) =>
    set((s) => {
      const already = s.typingIndicators.find(
        (t) => t.conversationId === conversationId && t.userId === userId
      );
      if (already) return s;
      return {
        typingIndicators: [
          ...s.typingIndicators,
          {
            conversationId,
            userId,
            displayName,
            startedAt: new Date().toISOString(),
          },
        ],
      };
    }),

  clearTyping: (conversationId, userId) =>
    set((s) => ({
      typingIndicators: s.typingIndicators.filter(
        (t) => !(t.conversationId === conversationId && t.userId === userId)
      ),
    })),

  // ─── Emoji preferences (Phase 3) ──────────────────────────────────────────

  setEmojiSet: (emojiSet) => {
    saveToStorage("slumbr:messenger:emojiSet", emojiSet);
    set({ emojiSet });
  },

  addCustomEmojiPack: (pack) =>
    set((s) => {
      const customEmojiPacks = [...s.customEmojiPacks, pack];
      saveToStorage("slumbr:messenger:customEmojiPacks", customEmojiPacks);
      return { customEmojiPacks };
    }),

  removeCustomEmojiPack: (packId) =>
    set((s) => {
      const customEmojiPacks = s.customEmojiPacks.filter((p) => p.id !== packId);
      saveToStorage("slumbr:messenger:customEmojiPacks", customEmojiPacks);
      return { customEmojiPacks };
    }),

  // ─── Encryption actions (Phase 7) ──────────────────────────────────────────

  initializeEncryptionKeys: async () => {
    const existing = await keyStore.hasIdentityKeys(DEV_USER_ID);
    if (existing) {
      const identityKeyPair = await keyStore.getIdentityKeyPair(DEV_USER_ID);
      if (identityKeyPair) {
        const settings: EncryptionSettings = {
          ...get().encryptionSettings,
          identityFingerprint: identityKeyPair.fingerprint,
          hasIdentityKeys: true,
        };
        saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
        set({ encryptionSettings: settings });
      }
      return;
    }

    // Generate new identity keys
    const identityKeyPair = await generateIdentityKeyPair();
    await keyStore.storeIdentityKeyPair(DEV_USER_ID, identityKeyPair);

    // Generate signed pre-key
    const signedPreKey = await generateSignedPreKey(identityKeyPair, 1);
    await keyStore.storeSignedPreKey(DEV_USER_ID, signedPreKey);

    // Generate one-time pre-keys
    const oneTimePreKeys = await generateOneTimePreKeys(1, 10);
    await keyStore.storeOneTimePreKeys(DEV_USER_ID, oneTimePreKeys);

    // Build and store our own pre-key bundle
    const bundle = await buildPreKeyBundle(
      DEV_USER_ID,
      identityKeyPair,
      signedPreKey,
      oneTimePreKeys[0]
    );
    await keyStore.storePreKeyBundle(bundle);

    const settings: EncryptionSettings = {
      ...get().encryptionSettings,
      identityFingerprint: identityKeyPair.fingerprint,
      hasIdentityKeys: true,
      lastKeyRotation: new Date().toISOString(),
    };
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    set({ encryptionSettings: settings });
  },

  enableEncryption: async (conversationId) => {
    const state = get();
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation) return;

    // Set status to initiating
    const encryptionStates = {
      ...state.encryptionStates,
      [conversationId]: {
        status: "initiating" as const,
        sessionId: null,
        isVerified: false,
        enabledAt: null,
      },
    };
    set({ encryptionStates });
    saveToStorage(STORAGE_KEYS.ENCRYPTION_STATES, encryptionStates);

    try {
      // Ensure our keys are initialized
      await get().initializeEncryptionKeys();

      const identityKeyPair = await keyStore.getIdentityKeyPair(DEV_USER_ID);
      const signedPreKeys = await keyStore.getSignedPreKeys(DEV_USER_ID);
      if (!identityKeyPair || signedPreKeys.length === 0) {
        throw new Error("Failed to load identity keys");
      }

      // For DM: encrypt with the other participant
      // For group: encrypt with each participant (simplified — one session per participant)
      const otherParticipants = conversation.participants.filter(
        (p) => p.userId !== DEV_USER_ID
      );

      if (otherParticipants.length === 0) return;

      // For now, use the first other participant (DM case)
      // Group E2E would need sender keys (Signal's group protocol) — future enhancement
      const remoteUserId = otherParticipants[0].userId;
      const sessionId = `session-${conversationId}-${generateUUID()}`;

      // In DEV_AUTH_BYPASS mode, create both sides of the session locally
      const { initiatorSession } = await createDevSession(
        sessionId,
        DEV_USER_ID,
        remoteUserId,
        identityKeyPair,
        signedPreKeys[0]
      );

      // Store the session
      await keyStore.storeSession(initiatorSession);

      // Update conversation and encryption state
      const newEncryptionStates = {
        ...get().encryptionStates,
        [conversationId]: {
          status: "active" as const,
          sessionId,
          isVerified: false,
          enabledAt: new Date().toISOString(),
        },
      };

      const conversations = get().conversations.map((c) =>
        c.id === conversationId ? { ...c, isEncrypted: true } : c
      );

      // Add system message about encryption
      const systemMessage: Message = {
        id: generateUUID(),
        conversationId,
        senderId: "system",
        content: {
          type: "system",
          text: "End-to-end encryption enabled. Messages in this conversation are now encrypted.",
          action: "encryption_enabled",
        } as SystemContent,
        status: "read",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const messages = {
        ...get().messages,
        [conversationId]: [...(get().messages[conversationId] ?? []), systemMessage],
      };

      set({ encryptionStates: newEncryptionStates, conversations, messages });
      saveToStorage(STORAGE_KEYS.ENCRYPTION_STATES, newEncryptionStates);
      persist({ conversations, messages });
    } catch (err) {
      console.error("Failed to enable encryption:", err);
      const errorStates = {
        ...get().encryptionStates,
        [conversationId]: {
          status: "error" as const,
          sessionId: null,
          isVerified: false,
          enabledAt: null,
          errorMessage: err instanceof Error ? err.message : "Unknown error",
        },
      };
      set({ encryptionStates: errorStates });
      saveToStorage(STORAGE_KEYS.ENCRYPTION_STATES, errorStates);
    }
  },

  disableEncryption: (conversationId) => {
    const encState = get().encryptionStates[conversationId];
    if (encState?.sessionId) {
      keyStore.deleteSession(encState.sessionId).catch(console.error);
    }

    const encryptionStates = { ...get().encryptionStates };
    delete encryptionStates[conversationId];

    const conversations = get().conversations.map((c) =>
      c.id === conversationId ? { ...c, isEncrypted: false } : c
    );

    // Add system message
    const systemMessage: Message = {
      id: generateUUID(),
      conversationId,
      senderId: "system",
      content: {
        type: "system",
        text: "End-to-end encryption disabled.",
        action: "encryption_disabled",
      } as SystemContent,
      status: "read",
      reactions: [],
      isPinned: false,
      isEdited: false,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const messages = {
      ...get().messages,
      [conversationId]: [...(get().messages[conversationId] ?? []), systemMessage],
    };

    set({ encryptionStates, conversations, messages });
    saveToStorage(STORAGE_KEYS.ENCRYPTION_STATES, encryptionStates);
    persist({ conversations, messages });
  },

  verifySession: (conversationId) => {
    const encState = get().encryptionStates[conversationId];
    if (!encState || encState.status !== "active") return;

    const encryptionStates = {
      ...get().encryptionStates,
      [conversationId]: { ...encState, isVerified: true },
    };

    // Add system message
    const systemMessage: Message = {
      id: generateUUID(),
      conversationId,
      senderId: "system",
      content: {
        type: "system",
        text: "Safety number verified. You have confirmed the identity of the other participant.",
        action: "encryption_verified",
      } as SystemContent,
      status: "read",
      reactions: [],
      isPinned: false,
      isEdited: false,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const messages = {
      ...get().messages,
      [conversationId]: [...(get().messages[conversationId] ?? []), systemMessage],
    };

    set({ encryptionStates, messages });
    saveToStorage(STORAGE_KEYS.ENCRYPTION_STATES, encryptionStates);
    persist({ conversations: get().conversations, messages });
  },

  getEncryptionDisplayInfo: async (conversationId) => {
    const encState = get().encryptionStates[conversationId];
    const settings = get().encryptionSettings;

    const info: EncryptionDisplayInfo = {
      status: encState?.status ?? "none",
      isVerified: encState?.isVerified ?? false,
      sessionId: encState?.sessionId ?? null,
      localFingerprint: settings.identityFingerprint,
      remoteFingerprint: null,
      safetyNumberDigits: null,
      safetyNumberDisplay: null,
      enabledAt: encState?.enabledAt ?? null,
    };

    if (encState?.sessionId) {
      try {
        const session = await keyStore.getSession(encState.sessionId);
        if (session) {
          const remoteFp = await computeFingerprint(session.remoteIdentityKey);
          info.remoteFingerprint = remoteFp;

          if (settings.identityFingerprint) {
            const safetyNumber = await generateSafetyNumber(
              settings.identityFingerprint,
              remoteFp
            );
            info.safetyNumberDigits = safetyNumber.digits;
            info.safetyNumberDisplay = safetyNumber.display;
          }
        }
      } catch (err) {
        console.error("Failed to get encryption display info:", err);
      }
    }

    return info;
  },

  encryptAndSendMessage: async (conversationId, content, options) => {
    const encState = get().encryptionStates[conversationId];
    if (!encState || encState.status !== "active" || !encState.sessionId) {
      // Fallback to unencrypted send
      return get().sendMessage(conversationId, content, options);
    }

    let envelopeStr: string;
    try {
      const session = await keyStore.getSession(encState.sessionId);
      if (!session) {
        return get().sendMessage(conversationId, content, options);
      }

      const plaintext = JSON.stringify(content);
      const { session: updatedSession, envelope } = await encryptMessage(
        session,
        plaintext
      );
      await keyStore.storeSession(updatedSession);
      envelopeStr = JSON.stringify(envelope);
    } catch (err) {
      console.error("Encryption failed, sending unencrypted:", err);
      return get().sendMessage(conversationId, content, options);
    }

    const messageId = generateUUID();
    // Use the server-aligned approximate time for optimistic stamping —
    // see comment on `serverTimeOffsetMs` for why local clocks aren't
    // safe to trust here. The real createdAt/updatedAt arrive in the
    // createChatMessage response and overwrite these values.
    const now = getApproxServerNowMs();
    const message: Message = {
      id: messageId,
      conversationId,
      senderId: getCurrentUserId(),
      content, // Store decrypted content locally for display
      status: "sent",
      ...(options?.replyToId ? { replyToId: options.replyToId } : {}),
      reactions: [],
      isPinned: false,
      isEdited: false,
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
      isEncrypted: true,
      encryptedEnvelope: envelopeStr,
    };

    set((s) => {
      const convMessages = [...(s.messages[conversationId] ?? []), message];
      const messages = { ...s.messages, [conversationId]: convMessages };
      const conversations = s.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, lastMessage: message, updatedAt: now }
          : c
      );
      persist({ conversations, messages });
      return { conversations, messages };
    });

    // Publish to backend so the server fans out `chat.message.created` over
    // Momento, persists the encrypted envelope, and triggers push
    // notifications. We strip the local plaintext from the server payload
    // — only the envelope ever leaves the device. The server stores
    // `isEncrypted` + `encryptedEnvelope`; other participants decrypt
    // locally when they receive the realtime event.
    const serverPayload: Record<string, unknown> = {
      ...stripClientMessageFields(message),
      content: { type: "encrypted" },
    };
    delete serverPayload.createdAt;
    delete serverPayload.updatedAt;
    createChatMessage(conversationId, serverPayload)
      .then((response) => {
        const r = response as
          | { createdAt?: number; updatedAt?: number }
          | undefined;
        if (r && r.createdAt !== undefined) {
          reconcileMessageTimestamps(
            conversationId,
            messageId,
            r.createdAt,
            r.updatedAt ?? r.createdAt
          );
        }
        // Delivered = server ack, not a timer.
        set((s) => {
          const convMessages = (s.messages[conversationId] ?? []).map((m) =>
            m.id === messageId && (m.status === "sent" || m.status === "sending")
              ? { ...m, status: "delivered" as const }
              : m
          );
          const messages = { ...s.messages, [conversationId]: convMessages };
          persist({ conversations: s.conversations, messages });
          return { messages };
        });
      })
      .catch((err) => {
        console.error("[encryptAndSend] createChatMessage failed:", err);
        set((s) => {
          const convMessages = (s.messages[conversationId] ?? []).map((m) =>
            m.id === messageId ? { ...m, status: "failed" as const } : m
          );
          return {
            messages: { ...s.messages, [conversationId]: convMessages },
          };
        });
      });

    return messageId;
  },

  decryptMessageContent: async (conversationId, messageId) => {
    const encState = get().encryptionStates[conversationId];
    if (!encState?.sessionId) return null;

    const messages = get().messages[conversationId] ?? [];
    const message = messages.find((m) => m.id === messageId);
    if (!message?.encryptedEnvelope) return null;
    // Already decrypted in-place once — no need to advance the ratchet again.
    if (
      message.content &&
      typeof message.content === "object" &&
      (message.content as { type?: string }).type !== "encrypted"
    ) {
      return null;
    }

    try {
      const session = await keyStore.getSession(encState.sessionId);
      if (!session) return null;

      const envelope = JSON.parse(message.encryptedEnvelope);
      const { session: updatedSession, plaintext } = await decryptMessage(
        session,
        envelope
      );

      await keyStore.storeSession(updatedSession);

      // Replace the encrypted-placeholder content with the decrypted
      // payload so the bubble renders text / image / gif / etc. We only
      // attempt the JSON parse — if the plaintext isn't a valid
      // MessageContent we leave the placeholder in place so the failure
      // is visible instead of silently swallowed.
      let parsed: MessageContent | null = null;
      try {
        parsed = JSON.parse(plaintext) as MessageContent;
      } catch {
        parsed = null;
      }
      if (parsed && typeof parsed === "object") {
        set((s) => {
          const list = s.messages[conversationId];
          if (!list) return s;
          const next = list.map((m) =>
            m.id === messageId ? { ...m, content: parsed as MessageContent } : m
          );
          const messages = { ...s.messages, [conversationId]: next };
          const updated = next.find((m) => m.id === messageId);
          const conversations = updated
            ? s.conversations.map((c) =>
                c.id === conversationId &&
                c.lastMessage?.id === messageId
                  ? { ...c, lastMessage: updated }
                  : c
              )
            : s.conversations;
          return { conversations, messages };
        });
      }
      return plaintext;
    } catch (err) {
      console.error("Decryption failed:", err);
      return null;
    }
  },

  // ─── PIN lock actions ──────────────────────────────────────────────────────

  setPinLock: async (pin) => {
    const pinHash = await hashPin(pin);
    const settings: EncryptionSettings = {
      ...get().encryptionSettings,
      pinLock: {
        ...get().encryptionSettings.pinLock,
        isEnabled: true,
        pinHash,
        failedAttempts: 0,
        lockoutUntil: null,
      },
    };
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    set({ encryptionSettings: settings });
  },

  removePinLock: () => {
    const settings: EncryptionSettings = {
      ...get().encryptionSettings,
      pinLock: DEFAULT_PIN_LOCK_CONFIG,
    };
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    set({ encryptionSettings: settings, isMessengerLocked: false });
  },

  verifyPin: async (pin) => {
    const { pinLock } = get().encryptionSettings;
    if (!pinLock.isEnabled || !pinLock.pinHash) return false;

    // Check lockout
    if (pinLock.lockoutUntil) {
      const lockoutEnd = new Date(pinLock.lockoutUntil).getTime();
      if (Date.now() < lockoutEnd) return false;
    }

    const pinHash = await hashPin(pin);
    if (pinHash === pinLock.pinHash) {
      // Reset failed attempts on success
      const settings: EncryptionSettings = {
        ...get().encryptionSettings,
        pinLock: { ...pinLock, failedAttempts: 0, lockoutUntil: null },
      };
      saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
      set({ encryptionSettings: settings });
      return true;
    }

    // Increment failed attempts
    const failedAttempts = pinLock.failedAttempts + 1;
    let lockoutUntil: string | null = null;
    if (failedAttempts >= pinLock.maxAttempts) {
      // Lock out for 1 minute per exceeded attempt
      const lockoutMs = (failedAttempts - pinLock.maxAttempts + 1) * 60 * 1000;
      lockoutUntil = new Date(Date.now() + lockoutMs).toISOString();
    }

    const settings: EncryptionSettings = {
      ...get().encryptionSettings,
      pinLock: { ...pinLock, failedAttempts, lockoutUntil },
    };
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    set({ encryptionSettings: settings });
    return false;
  },

  lockMessenger: () => {
    const { pinLock } = get().encryptionSettings;
    if (pinLock.isEnabled) {
      set({ isMessengerLocked: true });
    }
  },

  unlockMessenger: async (pin) => {
    const valid = await get().verifyPin(pin);
    if (valid) {
      set({ isMessengerLocked: false });
      return true;
    }
    return false;
  },

  updatePinLockConfig: (updates) => {
    const settings: EncryptionSettings = {
      ...get().encryptionSettings,
      pinLock: { ...get().encryptionSettings.pinLock, ...updates },
    };
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    set({ encryptionSettings: settings });
  },

  clearAllEncryptionData: async () => {
    await keyStore.clearAllKeys();
    const encryptionStates: Record<string, ConversationEncryptionState> = {};
    const settings = DEFAULT_ENCRYPTION_SETTINGS;
    const conversations = get().conversations.map((c) => ({
      ...c,
      isEncrypted: false,
    }));

    set({ encryptionStates, encryptionSettings: settings, conversations, isMessengerLocked: false });
    saveToStorage(STORAGE_KEYS.ENCRYPTION_STATES, encryptionStates);
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    persist({ conversations, messages: get().messages });
  },

  rotateKeys: async () => {
    const identityKeyPair = await keyStore.getIdentityKeyPair(DEV_USER_ID);
    if (!identityKeyPair) {
      await get().initializeEncryptionKeys();
      return;
    }

    // Generate new signed pre-key with incremented ID
    const existingSpks = await keyStore.getSignedPreKeys(DEV_USER_ID);
    const nextSpkId = existingSpks.length > 0
      ? Math.max(...existingSpks.map((k) => k.id)) + 1
      : 1;
    const newSignedPreKey = await generateSignedPreKey(identityKeyPair, nextSpkId);
    await keyStore.storeSignedPreKey(DEV_USER_ID, newSignedPreKey);

    // Generate new one-time pre-keys
    const existingOtks = await keyStore.getOneTimePreKeys(DEV_USER_ID);
    const nextOtkId = existingOtks.length > 0
      ? Math.max(...existingOtks.map((k) => k.id)) + 1
      : 1;
    const newOneTimePreKeys = await generateOneTimePreKeys(nextOtkId, 10);
    await keyStore.storeOneTimePreKeys(DEV_USER_ID, newOneTimePreKeys);

    // Update pre-key bundle
    const bundle = await buildPreKeyBundle(
      DEV_USER_ID,
      identityKeyPair,
      newSignedPreKey,
      newOneTimePreKeys[0]
    );
    await keyStore.storePreKeyBundle(bundle);

    const settings: EncryptionSettings = {
      ...get().encryptionSettings,
      lastKeyRotation: new Date().toISOString(),
    };
    saveToStorage(STORAGE_KEYS.ENCRYPTION_SETTINGS, settings);
    set({ encryptionSettings: settings });
  },

  // ─── Huddle01 Calls (Phase 8) ──────────────────────────────────────────────

  startCall: async (conversationId, type) => {
    const { activeCall, conversations } = get();
    if (activeCall) {
      set({ callError: "You are already in a call" });
      return;
    }

    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation) {
      set({ callError: "Conversation not found" });
      return;
    }

    const callId = generateUUID();
    const now = new Date().toISOString();

    // Set initial call session (creating state)
    const session: CallSession = {
      id: callId,
      conversationId,
      type,
      status: "creating",
      initiatorId: DEV_USER_ID,
      participantIds: conversation.participants.map((p) => p.userId),
      startedAt: now,
      roomId: "", // will be set after creation
    };

    set({
      activeCall: session,
      callError: null,
      isCallMinimized: false,
      localCallState: {
        isMicOn: true,
        isCameraOn: type === "video",
        isScreenSharing: false,
      },
      remoteParticipants: [],
    });

    try {
      // Dynamic import to avoid bundling Huddle01 when not needed
      const { createHuddleRoom, generateAccessToken, sendCallInvite } =
        await import("../huddle01/config");

      // 1. Create room
      const displayName =
        conversation.type === "group" && conversation.name
          ? conversation.name
          : "Slumbr Call";
      const roomId = await createHuddleRoom(
        `${displayName} — ${type === "video" ? "Video" : "Voice"} Call`
      );

      // 2. Generate access token
      const token = await generateAccessToken(roomId, getCurrentUserName());

      // 3. Update session with room details
      set({
        activeCall: {
          ...session,
          roomId,
          accessToken: token,
          status: "connecting",
        },
      });

      // 4. Add system message to conversation
      const systemMessage: Message = {
        id: generateUUID(),
        conversationId,
        senderId: DEV_USER_ID,
        content: {
          type: "system",
          text: `${type === "video" ? "Video" : "Voice"} call started`,
          action: "call_started",
        } as SystemContent,
        status: "sent",
        reactions: [],
        isPinned: false,
        isEdited: false,
        isDeleted: false,
        createdAt: now,
        updatedAt: now,
      };

      const messages = { ...get().messages };
      const convMessages = [...(messages[conversationId] ?? []), systemMessage];
      messages[conversationId] = convMessages;

      const updatedConversations = get().conversations.map((c) =>
        c.id === conversationId
          ? { ...c, lastMessage: systemMessage, updatedAt: now }
          : c
      );

      set({ messages, conversations: updatedConversations });
      persist({ conversations: updatedConversations, messages });
      await sendCallInvite({
        conversationId,
        roomId,
        callType: type,
        callerName: getCurrentUserName(),
      });
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Failed to start call";
      set({
        activeCall: null,
        callError: errorMsg,
      });
    }
  },

  acceptIncomingCall: async () => {
    const { incomingCall, activeCall } = get();
    if (!incomingCall || activeCall) {
      set({ incomingCall: null });
      return;
    }

    try {
      const { generateAccessToken } = await import("../huddle01/config");
      const { useBackgroundServiceManager } = await import("@slumbr/app-sdk");
      const { CallPersistentWidget } = await import("../components/call-persistent-widget");

      const token = await generateAccessToken(
        incomingCall.roomId,
        getCurrentUserName()
      );

      set({
        incomingCall: null,
        activeCall: {
          id: generateUUID(),
          conversationId: incomingCall.conversationId,
          type: incomingCall.type,
          status: "connecting",
          initiatorId: incomingCall.callerId,
          participantIds: [incomingCall.callerId, getCurrentUserId()],
          startedAt: new Date().toISOString(),
          roomId: incomingCall.roomId,
          accessToken: token,
        },
        callError: null,
        isCallMinimized: false,
        localCallState: {
          isMicOn: true,
          isCameraOn: incomingCall.type === "video",
          isScreenSharing: false,
        },
        remoteParticipants: [],
      });

      void useBackgroundServiceManager.getState().register({
        id: "messenger-active-call",
        appId: "messenger",
        persistentUI: CallPersistentWidget,
      });
    } catch (err) {
      set({
        incomingCall: null,
        callError: err instanceof Error ? err.message : "Failed to join call",
      });
    }
  },

  declineIncomingCall: () => {
    set({ incomingCall: null });
  },

  endCall: () => {
    const { activeCall, callHistory } = get();
    if (!activeCall) return;

    const now = new Date().toISOString();
    const startTime = new Date(activeCall.startedAt).getTime();
    const endTime = Date.now();
    const durationMs = endTime - startTime;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    const duration = `${minutes}:${seconds.toString().padStart(2, "0")}`;

    const endedCall: CallSession = {
      ...activeCall,
      status: "ended",
      endedAt: now,
      duration,
    };

    // Add to call history
    const newHistory = [endedCall, ...callHistory].slice(0, 50); // keep last 50

    // Add system message
    const systemMessage: Message = {
      id: generateUUID(),
      conversationId: activeCall.conversationId,
      senderId: DEV_USER_ID,
      content: {
        type: "system",
        text: `${activeCall.type === "video" ? "Video" : "Voice"} call ended (${duration})`,
        action: "call_ended",
      } as SystemContent,
      status: "sent",
      reactions: [],
      isPinned: false,
      isEdited: false,
      isDeleted: false,
      createdAt: now,
      updatedAt: now,
    };

    const messages = { ...get().messages };
    const convMessages = [
      ...(messages[activeCall.conversationId] ?? []),
      systemMessage,
    ];
    messages[activeCall.conversationId] = convMessages;

    const updatedConversations = get().conversations.map((c) =>
      c.id === activeCall.conversationId
        ? { ...c, lastMessage: systemMessage, updatedAt: now }
        : c
    );

    set({
      activeCall: null,
      remoteParticipants: [],
      isCallMinimized: false,
      callHistory: newHistory,
      messages,
      conversations: updatedConversations,
    });

    persist({ conversations: updatedConversations, messages });
    saveToStorage("slumbr:messenger:callHistory", newHistory);
  },

  setCallError: (error) => set({ callError: error }),

  toggleMic: () => {
    set((state) => ({
      localCallState: {
        ...state.localCallState,
        isMicOn: !state.localCallState.isMicOn,
      },
    }));
  },

  toggleCamera: () => {
    set((state) => ({
      localCallState: {
        ...state.localCallState,
        isCameraOn: !state.localCallState.isCameraOn,
      },
    }));
  },

  toggleScreenShare: () => {
    set((state) => ({
      localCallState: {
        ...state.localCallState,
        isScreenSharing: !state.localCallState.isScreenSharing,
      },
    }));
  },

  updateRemoteParticipant: (peerId, updates) => {
    set((state) => ({
      remoteParticipants: state.remoteParticipants.map((p) =>
        p.peerId === peerId ? { ...p, ...updates } : p
      ),
    }));
  },

  addRemoteParticipant: (participant) => {
    set((state) => ({
      remoteParticipants: [
        ...state.remoteParticipants.filter((p) => p.peerId !== participant.peerId),
        participant,
      ],
    }));
  },

  removeRemoteParticipant: (peerId) => {
    set((state) => ({
      remoteParticipants: state.remoteParticipants.filter((p) => p.peerId !== peerId),
    }));
  },

  setCallStatus: (status) => {
    set((state) => ({
      activeCall: state.activeCall ? { ...state.activeCall, status } : null,
    }));
  },

  minimizeCall: () => set({ isCallMinimized: true }),
  maximizeCall: () => set({ isCallMinimized: false }),

  // ─── Computed ──────────────────────────────────────────────────────────────

  getActiveConversation: () => {
    const { conversations, activeConversationId } = get();
    return conversations.find((c) => c.id === activeConversationId);
  },

  getConversationMessages: (id) => {
    return (get().messages[id] ?? []).filter((m) => !m.isDeleted);
  },

  getFilteredConversations: () => {
    const { conversations, searchQuery } = get();
    let filtered = conversations.filter((c) => !c.isArchived);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((c) => {
        // Search by name
        const name = get().getConversationDisplayName(c).toLowerCase();
        if (name.includes(q)) return true;
        // Search by last message content
        if (
          c.lastMessage?.content.type === "text" &&
          c.lastMessage.content.text.toLowerCase().includes(q)
        )
          return true;
        return false;
      });
    }

    // Sort: pinned first, then by updatedAt descending
    filtered.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    return filtered;
  },

  getUnreadCount: () => {
    return get().conversations.reduce((sum, c) => sum + c.unreadCount, 0);
  },

  getConversationDisplayName: (conversation) => {
    if (conversation.type === "group" && conversation.name) {
      return conversation.name;
    }
    // DM: show the OTHER participant's name. Must compare against the real
    // signed-in user id — the old DEV_USER_ID comparison matched nobody in
    // production, so DMs were labeled with participants[0] (often yourself).
    const me = getCurrentUserId();
    const other = conversation.participants.find((p) => p.userId !== me);
    return other?.displayName ?? "Unknown";
  },

  getTypingUsers: (conversationId) => {
    const me = getCurrentUserId();
    return get().typingIndicators.filter(
      (t) => t.conversationId === conversationId && t.userId !== me
    );
  },
}));

// Reset all in-memory messenger state. Used both on logout and on profile
// (persona) switch — both transitions must guarantee no data bleed from
// the previous identity into the next one.
export function resetMessengerState() {
  // CRITICAL: cancel any in-flight IDB mirror write BEFORE we let the new
  // profile pick up, otherwise old profile data can land in the new
  // profile's local cache bucket.
  if (idbPersistTimer) {
    clearTimeout(idbPersistTimer);
    idbPersistTimer = null;
  }
  // Invalidate any in-flight conversation fetch — it belongs to the profile
  // we're leaving and must not write into the next profile's store.
  messengerResetEpoch += 1;
  serverTimeOffsetMs = 0;
  historyFetches.clear();
  pendingConversationCreates.clear();
  pendingMessagePublishes.clear();
  for (const timer of readTimers.values()) clearTimeout(timer);
  readTimers.clear();
  lastMarkReadSentAt.clear();
  inFlightConversationsFetch = null;
  lastConversationsSyncAt = 0;
  // Reset the IDB-mirror diff trackers so the next persist() write is
  // forced to send fresh data to the new profile's DB.
  prevPersistedConversations = null;
  for (const key of Object.keys(prevPersistedMessagesByConv)) {
    delete prevPersistedMessagesByConv[key];
  }
  useMessengerStore.setState({
    conversations: [],
    messages: {},
    messagesPaging: {},
    hydratedConversationIds: new Set<string>(),
    activeConversationId: null,
    typingIndicators: [],
    error: null,
    isLoading: false,
    messageErrors: {},
    messageLoading: {},
  });
}

// Kick off IDB hydration as soon as the store is alive so the conversation
// list + recent messages can paint before the network fetch resolves.
// Fire-and-forget — the action handles its own errors.
if (typeof window !== "undefined" && !DEV_AUTH_BYPASS) {
  // Bind the IDB cache to whichever profile is currently active before
  // running the first hydrate, otherwise we'd open the "default" bucket
  // and serve stale data to whoever is signed in.
  setActiveProfile(useProfileSwitcherStore.getState().activeProfileId);
  void useMessengerStore.getState().hydrateFromCache();
}

// Profile (persona) switch: wipe in-memory + repoint the IDB cache at the
// new profile's DB, then re-hydrate and refetch. The auth session itself
// doesn't change — we're still the same Cognito user — so the logout
// subscriber below does not fire here. This subscriber handles the
// switch-while-signed-in case explicitly.
if (typeof window !== "undefined") {
  let prevProfileId = useProfileSwitcherStore.getState().activeProfileId;
  useProfileSwitcherStore.subscribe((state) => {
    const nextProfileId = state.activeProfileId;
    if (nextProfileId === prevProfileId) return;
    prevProfileId = nextProfileId;
    setActiveProfile(nextProfileId);
    resetMessengerState();
    if (!DEV_AUTH_BYPASS && nextProfileId) {
      const store = useMessengerStore.getState();
      void store.hydrateFromCache();
      void store.fetchConversations();
    }
  });
}

// Wipe the messenger IDB cache + in-memory state on logout so the next
// user signing in on this device never sees the previous account's chats.
// We watch the auth store for an authenticated → unauthenticated transition;
// signing in for the first time on this device also fires this once with
// `wasAuthed = false`, which is a no-op.
if (typeof window !== "undefined") {
  let wasAuthed = useUserStore.getState().isAuthenticated;
  useUserStore.subscribe((state) => {
    const isAuthed = state.isAuthenticated;
    if (wasAuthed && !isAuthed) {
      // Wipe the active profile's cache. Other profiles' caches survive —
      // each lives in its own DB, so swapping accounts on this device
      // doesn't leak data across them.
      void clearMessengerCache();
      resetMessengerState();
    }
    wasAuthed = isAuthed;
  });
}
